import { supabase } from './supabase';
import { ExerciseService } from './exerciseService';
import {
  Workout,
  Exercise,
  WorkoutBlock,
  ExerciseSet,
  WorkoutPlan,
  PlanWorkout,
  MemberWorkoutPlan,
  WorkoutSession,
  WorkoutWithBlocks,
  WorkoutSummary,
  WorkoutPlanWithSchedule,
  CreateWorkoutData,
  CreateWorkoutBlockData,
  CreateExerciseSetData,
  CreateWorkoutPlanData,
  CreatePlanWorkoutData,
  CreateMemberWorkoutPlanData,
} from '../types/dataModel';
import { getExecutionStepsForDisplay, stepsToDescription } from '../utils/exerciseDisplayUtils';
import { addDaysUtc, parseUtcDateKey, toUtcDateKey } from '../utils/dateKey';
import { displayNameFromProfile } from '../pages/account/accountShared';
import { isAllowlistedWorkoutBannerUrl } from './workoutBannerImage';
import { messageFromUnknownError } from '../utils/unknownErrorMessage';
import { sessionIsFromTrainer, trainerIdsMatch } from './memberLast7DaysActivity';

/** Payload passed to dashboard Realtime subscription callback. */
export type DashboardChangePayload = {
  table: 'users_workoutplan' | 'workout_session';
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
};

const WORKOUT_BY_ID_CACHE_TTL_MS = 30_000;

/** Title-case + trim a muscle_group / equipment value for client-side tag display. Returns '' if empty. */
function normalizeTagValue(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word.length === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join(' ');
}

/**
 * All distinct keys sorted by descending count; ties broken alphabetically. Returns [] for empty/undefined map.
 * Callers (e.g. the Library Workouts table) decide how many to render — see §7.1 of the redesign handoff.
 */
function tagsSortedByCount(counts: Map<string, number> | undefined): string[] {
  if (!counts || counts.size === 0) return [];
  return Array.from(counts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    })
    .map(([key]) => key);
}

/** Stable UUID for exercise_group_id when not provided (DB requires a value). */
function randomExerciseGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Return shape from `upsert_trainer_workout` RPC (PostgREST jsonb keys). */
export type UpsertTrainerWorkoutRpcResult = {
  workout_id: string;
  updated_at: string;
  blocks_written: number;
  exercise_set_rows_written: number;
  exercise_set_rows_deleted: number;
  blocks_unlinked: number;
  block_id_map: Record<string, string>;
  set_id_map: Record<string, string>;
};

/** Return shape from `duplicate_trainer_workout` RPC (PostgREST jsonb keys). */
export type DuplicateTrainerWorkoutRpcResult = {
  new_workout_id: string;
  source_workout_id: string;
  new_workout_name: string;
  blocks_copied: number;
  exercise_set_rows_copied: number;
  distinct_exercise_count: number;
};

/** One schedule row when duplicating a plan (IDs only — no workout graph). */
export type PlanDuplicateScheduleRow = { workoutId: string; dayNo: number; orderInDay: number };

export type WorkoutPlanDuplicateSource = {
  id: string;
  name: string;
  description: string | null;
  totalNoOfDays: number;
  difficulty: string | null | undefined;
  createdBy: string;
  isPublic: boolean;
  schedule: PlanDuplicateScheduleRow[];
};

export type DuplicateTrainerWorkoutPlanResult = {
  newPlanId: string;
  sourcePlanId: string;
  newPlanName: string;
  scheduleRowsCopied: number;
};

/** Map Supabase RPC / Postgres errors from duplicate flow to trainer-facing copy. Handoff §8. */
export function parseDuplicateWorkoutError(error: unknown): string {
  const raw = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
  const message = typeof raw.message === 'string' ? raw.message : '';
  const code = typeof raw.code === 'string' ? raw.code : '';
  const details = typeof raw.details === 'string' ? raw.details : '';
  const haystack = `${message} ${details}`;

  if (/NOT_AUTHENTICATED|not authenticated\b|jwt/i.test(message) || (!message && code === '401')) {
    return 'Please sign in to duplicate workouts.';
  }
  if (/SOURCE_WORKOUT_NOT_FOUND/i.test(message) || code === 'P0002') {
    return 'That workout isn’t available to copy.';
  }
  if (code === '42501' || /row-level security/i.test(message) || /\brls\b/i.test(haystack)) {
    return 'Could not save copy. Contact support.';
  }
  if (
    code === '23505' ||
    /unique constraint|duplicate key/i.test(`${message} ${details}`)
  ) {
    return 'Duplicate failed due to ordering conflict — try again or contact support.';
  }
  return 'Something went wrong while duplicating. Please try again.';
}

/** Map Supabase RPC / Postgres errors from upsert save to trainer-facing copy. Handoff §5.4. */
export function parseUpsertTrainerWorkoutError(error: unknown): string {
  const raw = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
  const message = typeof raw.message === 'string' ? raw.message : '';
  const code = typeof raw.code === 'string' ? raw.code : '';
  const details = typeof raw.details === 'string' ? raw.details : '';
  const haystack = `${message} ${details}`;

  if (/NOT_AUTHENTICATED|not authenticated\b|jwt/i.test(message) || (!message && code === '401')) {
    return 'Please sign in to save workouts.';
  }
  if (/WORKOUT_NOT_FOUND/i.test(message) || code === 'P0002') {
    return 'That workout isn’t available to save.';
  }
  if (/WORKOUT_NOT_OWNED/i.test(message)) {
    return 'You can only save workouts you created.';
  }
  if (/INVALID_BANNER_URL/i.test(message)) {
    return 'Cover image must be from the gallery.';
  }
  if (/INVALID_PAYLOAD/i.test(message)) {
    return 'Could not save workout. Check the workout name and try again.';
  }
  if (code === '42501' || /row-level security/i.test(message) || /\brls\b/i.test(haystack)) {
    return 'Could not save workout. Contact support.';
  }
  if (haystack.includes('block_type_check')) {
    return 'One of your blocks has an invalid type. Please reselect the block type and try again.';
  }
  if (
    code === '23505' ||
    /unique constraint|duplicate key/i.test(`${message} ${details}`)
  ) {
    return 'Save failed due to ordering conflict — try again.';
  }
  if (/Workout cover image must be a gallery image/i.test(message)) {
    return 'Cover image must be from the gallery.';
  }
  if (
    code === '57014' ||
    /statement timeout|canceling statement due to statement timeout/i.test(haystack)
  ) {
    return 'Save is taking longer than expected. Wait a moment and try again.';
  }
  if (/INVALID_RPC_PAYLOAD|invalid payload|payload missing fields/i.test(`${message} ${details}`)) {
    return 'Save completed but the server response was unexpected. Refresh the page and try again.';
  }
  if (
    code === '42883' ||
    code === 'PGRST202' ||
    code === 'PGRST301' ||
    /could not find the function/i.test(haystack) ||
    (/does not exist/i.test(haystack) && /upsert_trainer_workout/i.test(haystack))
  ) {
    return 'Fast save is not enabled on this database yet. Using the slower save path — try again in a moment.';
  }
  const full = messageFromUnknownError(error);
  if (process.env.NODE_ENV === 'development' && full && full !== 'Unknown error') {
    console.warn('[parseUpsertTrainerWorkoutError] unmapped:', error, full);
  }
  return 'Failed to save workout. Please try again.';
}

/** True when PostgREST/Postgres does not expose `upsert_trainer_workout` — use REST orchestration. */
function shouldFallbackUpsertTrainerWorkoutRpc(error: unknown): boolean {
  const raw = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
  const code = typeof raw.code === 'string' ? raw.code : '';
  const message = typeof raw.message === 'string' ? raw.message : '';
  const m = message.toLowerCase();

  if (code === '42883') return true;
  if (code === 'PGRST202' || code === 'PGRST301') return true;
  if (m.includes('could not find the function') && m.includes('upsert_trainer_workout')) return true;
  if (m.includes('does not exist') && m.includes('upsert_trainer_workout')) return true;
  if (m.includes('schema cache') && m.includes('upsert_trainer_workout')) return true;
  if (m.includes('function') && m.includes('upsert_trainer_workout') && m.includes('does not exist')) {
    return true;
  }
  return false;
}

function rpcJsonNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return Number.NaN;
}

function parseJsonbIdMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim() !== '') out[k] = v;
  }
  return out;
}

function parseUpsertTrainerWorkoutRpcPayload(data: unknown): UpsertTrainerWorkoutRpcResult {
  if (!data || typeof data !== 'object') {
    throw Object.assign(new Error('upsert_trainer_workout returned invalid payload'), {
      code: 'INVALID_RPC_PAYLOAD',
    });
  }
  const d = data as Record<string, unknown>;
  const workoutId = typeof d.workout_id === 'string' ? d.workout_id : null;
  const updatedAt =
    typeof d.updated_at === 'string'
      ? d.updated_at
      : d.updated_at != null
        ? String(d.updated_at)
        : null;
  const blocksWritten = rpcJsonNumber(d.blocks_written);
  const eibWritten = rpcJsonNumber(d.exercise_set_rows_written);
  const eibDeleted = rpcJsonNumber(d.exercise_set_rows_deleted);
  const blocksUnlinked = rpcJsonNumber(d.blocks_unlinked);
  if (
    !workoutId ||
    !updatedAt ||
    !Number.isFinite(blocksWritten) ||
    !Number.isFinite(eibWritten) ||
    !Number.isFinite(eibDeleted) ||
    !Number.isFinite(blocksUnlinked)
  ) {
    throw Object.assign(new Error('upsert_trainer_workout payload missing fields'), {
      code: 'INVALID_RPC_PAYLOAD',
    });
  }
  return {
    workout_id: workoutId,
    updated_at: updatedAt,
    blocks_written: blocksWritten,
    exercise_set_rows_written: eibWritten,
    exercise_set_rows_deleted: eibDeleted,
    blocks_unlinked: blocksUnlinked,
    block_id_map: parseJsonbIdMap(d.block_id_map),
    set_id_map: parseJsonbIdMap(d.set_id_map),
  };
}

function isPersistedWorkoutEntityId(id: string | null | undefined): boolean {
  if (!id || id.startsWith('temp-')) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

/** Build `p_payload` for `upsert_trainer_workout` from sanitized workout graph (snake_case). */
function buildUpsertTrainerWorkoutPayload(workoutData: WorkoutWithBlocks): Record<string, unknown> {
  const bannerUrl = (workoutData.bannerImageUrl || '').trim();
  const blocks = (workoutData.workoutBlocks ?? []).map((block) => {
    const blockId = block.id;
    const isTempBlock = blockId?.startsWith('temp-');
    const exerciseSets = (block.exerciseSets ?? [])
      .filter((set) => set.exerciseId)
      .map((exerciseSet, setIndex) => {
        const setId = exerciseSet.id;
        const row: Record<string, unknown> = {
          exercise_id: exerciseSet.exerciseId,
          order: setIndex + 1,
          rest_seconds: exerciseSet.restSeconds ?? 0,
          note:
            exerciseSet.note !== undefined &&
            exerciseSet.note !== null &&
            String(exerciseSet.note).trim() !== ''
              ? String(exerciseSet.note).trim()
              : null,
          exercise_group_id: exerciseSet.exerciseGroupId ?? null,
        };
        if (setId && isPersistedWorkoutEntityId(setId)) {
          row.id = setId;
        } else if (setId) {
          row.id = setId;
        }
        const rt = typeof exerciseSet.repText === 'string' ? exerciseSet.repText.trim() : '';
        row.rep_text = rt === '' ? null : rt;
        const w = exerciseSet.weightKg;
        if (w !== undefined && w !== null && !Number.isNaN(Number(w))) {
          row.weight_kg = Number(w);
        } else {
          row.weight_kg = null;
        }
        const repValue = exerciseSet.rep;
        if (
          repValue !== undefined &&
          repValue !== null &&
          typeof repValue === 'number' &&
          repValue > 0
        ) {
          row.rep = repValue;
        }
        const durationValue = exerciseSet.durationSeconds;
        if (
          durationValue !== undefined &&
          durationValue !== null &&
          typeof durationValue === 'number' &&
          durationValue > 0
        ) {
          row.duration_seconds = durationValue;
        }
        return row;
      });

    const blockNote =
      block.note !== undefined && block.note !== null && String(block.note).trim() !== ''
        ? String(block.note).trim()
        : null;

    const blockRow: Record<string, unknown> = {
      sets: block.sets != null && Number(block.sets) >= 1 ? Number(block.sets) : 1,
      type: block.type,
      name: block.name,
      rest_seconds: block.restSeconds ?? null,
      note: blockNote,
      exercise_sets: exerciseSets,
    };

    if (blockId && isPersistedWorkoutEntityId(blockId)) {
      blockRow.id = blockId;
    } else if (blockId?.startsWith('temp-')) {
      blockRow.client_id = blockId;
    }

    return blockRow;
  });

  return {
    name: workoutData.name,
    note: workoutData.note ?? null,
    estimated_duration_minutes: workoutData.estimatedDurationMinutes ?? null,
    difficulty: workoutData.difficulty ?? null,
    is_public: workoutData.isPublic ?? false,
    banner_image_url: bannerUrl,
    blocks,
  };
}

function patchWorkoutIdsFromPayload(
  workoutData: WorkoutWithBlocks,
  workoutId: string,
  rpc: UpsertTrainerWorkoutRpcResult,
): WorkoutWithBlocks {
  const blockMap = rpc.block_id_map;
  const setMap = rpc.set_id_map;
  const patchedBlocks = (workoutData.workoutBlocks ?? []).map((block) => {
    let blockId = block.id;
    if (blockId && blockMap[blockId]) {
      blockId = blockMap[blockId];
    }
    const exerciseSets = (block.exerciseSets ?? []).map((set) => {
      let setId = set.id;
      if (setId && setMap[setId]) {
        setId = setMap[setId];
      }
      return {
        ...set,
        id: setId,
        blockId: blockId ?? set.blockId,
      };
    });
    return {
      ...block,
      id: blockId,
      exerciseSets,
    };
  });

  return {
    ...workoutData,
    id: workoutId,
    updatedAt: rpc.updated_at,
    workoutBlocks: patchedBlocks,
  };
}

function parseDuplicateTrainerWorkoutRpcPayload(data: unknown): DuplicateTrainerWorkoutRpcResult {
  if (!data || typeof data !== 'object') {
    throw Object.assign(new Error('duplicate_trainer_workout returned invalid payload'), {
      code: 'INVALID_RPC_PAYLOAD',
    });
  }
  const d = data as Record<string, unknown>;
  const nw = typeof d.new_workout_id === 'string' ? d.new_workout_id : null;
  const name = typeof d.new_workout_name === 'string' ? d.new_workout_name : null;
  const src = typeof d.source_workout_id === 'string' ? d.source_workout_id : null;
  const blocksCopied = typeof d.blocks_copied === 'number' ? d.blocks_copied : Number.NaN;
  const eibCopied =
    typeof d.exercise_set_rows_copied === 'number' ? d.exercise_set_rows_copied : Number.NaN;
  const distinct =
    typeof d.distinct_exercise_count === 'number' ? d.distinct_exercise_count : Number.NaN;
  if (
    !nw ||
    !name ||
    !src ||
    !Number.isFinite(blocksCopied) ||
    !Number.isFinite(eibCopied) ||
    !Number.isFinite(distinct)
  ) {
    throw Object.assign(new Error('duplicate_trainer_workout payload missing fields'), {
      code: 'INVALID_RPC_PAYLOAD',
    });
  }
  return {
    new_workout_id: nw,
    source_workout_id: src,
    new_workout_name: name,
    blocks_copied: blocksCopied,
    exercise_set_rows_copied: eibCopied,
    distinct_exercise_count: distinct,
  };
}

/** True when Postgres/PostgREST does not expose `duplicate_trainer_workout_plan` — use client batch (Option A). */
function shouldFallbackDuplicateTrainerWorkoutPlanRpc(error: unknown): boolean {
  const raw = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
  const code = typeof raw.code === 'string' ? raw.code : '';
  const message = typeof raw.message === 'string' ? raw.message : '';
  const m = message.toLowerCase();

  if (code === '42883') return true;
  if (code === 'PGRST202' || code === 'PGRST301') return true;
  if (m.includes('could not find the function') && m.includes('duplicate_trainer_workout_plan')) return true;
  if (m.includes('does not exist') && m.includes('duplicate_trainer_workout_plan')) return true;
  if (m.includes('schema cache') && m.includes('duplicate_trainer_workout_plan')) return true;
  if (m.includes('function') && m.includes('duplicate_trainer_workout_plan') && m.includes('does not exist')) {
    return true;
  }
  return false;
}

function parseDuplicateTrainerWorkoutPlanRpcPayload(data: unknown): {
  new_plan_id: string;
  source_plan_id: string;
  new_plan_name: string;
  schedule_rows_copied: number;
} {
  if (!data || typeof data !== 'object') {
    throw Object.assign(new Error('duplicate_trainer_workout_plan returned invalid payload'), {
      code: 'INVALID_RPC_PAYLOAD',
    });
  }
  const d = data as Record<string, unknown>;
  const np = typeof d.new_plan_id === 'string' ? d.new_plan_id : null;
  const name = typeof d.new_plan_name === 'string' ? d.new_plan_name : null;
  const src = typeof d.source_plan_id === 'string' ? d.source_plan_id : null;
  const copied =
    typeof d.schedule_rows_copied === 'number' ? d.schedule_rows_copied : Number.NaN;
  if (!np || !name || !src || !Number.isFinite(copied)) {
    throw Object.assign(new Error('duplicate_trainer_workout_plan payload missing fields'), {
      code: 'INVALID_RPC_PAYLOAD',
    });
  }
  return {
    new_plan_id: np,
    source_plan_id: src,
    new_plan_name: name,
    schedule_rows_copied: copied,
  };
}

type PlanScheduleDbRow = {
  id?: string;
  workoutplan_id?: string;
  workout_id: string;
  day_no: number;
  order_in_day?: number | null;
  created_at?: string;
};

/** null = unknown; false = column missing (pre-migration DB). */
let planOrderInDayColumnAvailable: boolean | null = null;

function isMissingOrderInDayError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  return e.code === '42703' && Boolean(e.message?.includes('order_in_day'));
}

function disableOrderInDaySupport(): void {
  if (planOrderInDayColumnAvailable === false) return;
  planOrderInDayColumnAvailable = false;
  console.warn(
    '[WorkoutService] workouts_in_workoutplan.order_in_day is missing. Run database/add_order_in_day_workouts_in_workoutplan.sql in Supabase SQL editor to enable multiple workouts per day.',
  );
}

function orderInDayEnabled(): boolean {
  return planOrderInDayColumnAvailable !== false;
}

function mapPlanScheduleRow(pw: PlanScheduleDbRow) {
  return {
    id: pw.id as string,
    workoutPlanId: pw.workoutplan_id as string,
    workoutId: pw.workout_id,
    dayNo: pw.day_no,
    orderInDay: pw.order_in_day ?? 1,
    createdAt: pw.created_at as string,
  };
}

async function fetchWorkoutsInPlanSchedule(
  workoutPlanId: string,
  select: string,
): Promise<{ data: PlanScheduleDbRow[]; error: unknown | null }> {
  const query = (withOrderInDay: boolean, selectClause: string) => {
    let q = supabase
      .from('workouts_in_workoutplan')
      .select(selectClause)
      .eq('workoutplan_id', workoutPlanId)
      .order('day_no', { ascending: true });
    if (withOrderInDay && orderInDayEnabled()) {
      q = q.order('order_in_day', { ascending: true });
    }
    return q;
  };

  let { data, error } = await query(true, select);
  if (error && isMissingOrderInDayError(error)) {
    disableOrderInDaySupport();
    const fallbackSelect = select
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== 'order_in_day')
      .join(', ');
    ({ data, error } = await query(false, fallbackSelect || select));
  }

  return { data: (data ?? []) as unknown as PlanScheduleDbRow[], error };
}

export class WorkoutService {
  private static workoutByIdCache = new Map<string, { data: WorkoutWithBlocks; timestamp: number }>();

  /** False when DB has not run add_order_in_day_workouts_in_workoutplan.sql yet. */
  static planScheduleSupportsOrderInDay(): boolean {
    return orderInDayEnabled();
  }

  /** Update schedule row day/slot; omits order_in_day when column is unavailable. */
  static async updatePlanWorkoutSlot(
    planWorkoutId: string,
    patch: { dayNo: number; orderInDay: number },
  ): Promise<void> {
    const updatePayload: { day_no: number; order_in_day?: number } = { day_no: patch.dayNo };
    if (orderInDayEnabled()) {
      updatePayload.order_in_day = patch.orderInDay;
    }
    let { error } = await supabase
      .from('workouts_in_workoutplan')
      .update(updatePayload)
      .eq('id', planWorkoutId);
    if (error && isMissingOrderInDayError(error)) {
      disableOrderInDaySupport();
      ({ error } = await supabase
        .from('workouts_in_workoutplan')
        .update({ day_no: patch.dayNo })
        .eq('id', planWorkoutId));
    }
    if (error) throw error;
  }

  /** Invalidate cached workout so next getWorkoutById refetches (e.g. after exercise override save). */
  static invalidateWorkoutCache(workoutId?: string): void {
    if (workoutId) {
      this.workoutByIdCache.delete(workoutId);
    } else {
      this.workoutByIdCache.clear();
    }
  }

  /** Replace embedded `exercise` on every set with the given id (e.g. after save + getExerciseById). */
  static patchExerciseIntoWorkout(workout: WorkoutWithBlocks, exerciseId: string, exercise: Exercise): WorkoutWithBlocks {
    return {
      ...workout,
      workoutBlocks: (workout.workoutBlocks ?? []).map((block: WorkoutBlock & { exerciseSets?: any[] }) => ({
        ...block,
        exerciseSets: (block.exerciseSets ?? []).map((set: any) =>
          set.exerciseId === exerciseId ? { ...set, exercise } : set
        ),
      })),
    };
  }

  /**
   * Ensures session.workout has full workoutBlocks (fixes light/cached shapes, stale workout-by-id cache, or swallowed load errors).
   * Clones before applying snapshots so parallel hydrates for the same workoutId never share mutated state.
   */
  static async hydrateSessionWorkoutIfNeeded(
    session: WorkoutSession & { workout?: WorkoutWithBlocks | any }
  ): Promise<WorkoutSession & { workout?: WorkoutWithBlocks }> {
    if (!session.workoutId) return session;
    const w = session.workout as any;
    if (w && !w.workoutBlocks && Array.isArray(w.workout_blocks)) {
      w.workoutBlocks = w.workout_blocks;
    }
    if (w?.workoutBlocks?.length) {
      for (const block of w.workoutBlocks as any[]) {
        if (block && !block.exerciseSets && Array.isArray(block.exercise_sets)) {
          block.exerciseSets = block.exercise_sets;
        }
      }
    }
    const blocks = session.workout?.workoutBlocks;
    if (Array.isArray(blocks) && blocks.length > 0) return session;

    this.invalidateWorkoutCache(session.workoutId);
    try {
      const full = await this.getWorkoutById(session.workoutId);
      if (!full) return session;

      const forSession: WorkoutWithBlocks = {
        ...full,
        workoutBlocks: (full.workoutBlocks ?? []).map((block) => ({
          ...block,
          exerciseSets: (block.exerciseSets ?? []).map((set) => ({ ...set })),
        })),
      };

      const snapshots = await this.getSessionExerciseSnapshots(session.id);
      if (snapshots.length > 0) {
        this.applySnapshotsToWorkout(forSession, snapshots);
      }

      return { ...session, workout: forSession };
    } catch (e) {
      console.error('hydrateSessionWorkoutIfNeeded:', e);
      return session;
    }
  }

  private static mapEibRowToExerciseSet(
    set: any,
    exerciseResolved: any
  ): ExerciseSet & { exercise?: any } {
    return {
      id: set.id,
      blockId: set.block_id,
      exerciseId: set.exercise_id,
      exercise: exerciseResolved,
      order: set.order,
      rep: set.rep,
      repText: set.rep_text ?? undefined,
      durationSeconds: set.duration_seconds,
      restSeconds: set.rest_seconds,
      note: set.note,
      weightKg:
        set.weight_kg != null && set.weight_kg !== ''
          ? Number(set.weight_kg)
          : undefined,
      exerciseGroupId: set.exercise_group_id ?? undefined,
      createdAt: set.created_at,
      updatedAt: set.updated_at,
    };
  }

  /** Build insert/update payload for exercises_in_block from app exercise set. */
  private static eibPayloadFromExerciseSet(exerciseSet: any, setOrder: number, blockId: string): any {
    const insertData: any = {
      block_id: blockId,
      exercise_id: exerciseSet.exerciseId,
      order: setOrder,
      rest_seconds: exerciseSet.restSeconds ?? 0,
      note:
        exerciseSet.note !== undefined && exerciseSet.note !== null && String(exerciseSet.note).trim() !== ''
          ? String(exerciseSet.note).trim()
          : null,
      exercise_group_id: exerciseSet.exerciseGroupId || randomExerciseGroupId(),
    };
    const rt =
      typeof exerciseSet.repText === 'string' ? exerciseSet.repText.trim() : '';
    insertData.rep_text = rt === '' ? null : rt;
    const w = exerciseSet.weightKg;
    if (w !== undefined && w !== null && w !== '' && !Number.isNaN(Number(w))) {
      insertData.weight_kg = Number(w);
    } else {
      insertData.weight_kg = null;
    }
    const repValue = exerciseSet.rep;
    if (repValue !== undefined && repValue !== null && typeof repValue === 'number' && repValue > 0) {
      insertData.rep = repValue;
    }
    const durationValue = exerciseSet.durationSeconds;
    if (
      durationValue !== undefined &&
      durationValue !== null &&
      typeof durationValue === 'number' &&
      durationValue > 0
    ) {
      insertData.duration_seconds = durationValue;
    }
    return insertData;
  }

  private static eibUpdatePayloadFromExerciseSet(exerciseSet: any, setOrder: number): any {
    const note =
      exerciseSet.note !== undefined && exerciseSet.note !== null && String(exerciseSet.note).trim() !== ''
        ? String(exerciseSet.note).trim()
        : null;
    const rt =
      typeof exerciseSet.repText === 'string' ? exerciseSet.repText.trim() : '';
    const w = exerciseSet.weightKg;
    const weightKg =
      w !== undefined && w !== null && w !== '' && !Number.isNaN(Number(w)) ? Number(w) : null;
    const repValue = exerciseSet.rep;
    const rep =
      repValue !== undefined && repValue !== null && !isNaN(Number(repValue)) && Number(repValue) > 0
        ? Number(repValue)
        : null;
    const durationValue = exerciseSet.durationSeconds;
    const durationSeconds =
      durationValue !== undefined &&
      durationValue !== null &&
      !isNaN(Number(durationValue)) &&
      Number(durationValue) > 0
        ? Number(durationValue)
        : null;
    return {
      exercise_id: exerciseSet.exerciseId,
      order: setOrder,
      rest_seconds: exerciseSet.restSeconds ?? 0,
      note,
      updated_at: new Date().toISOString(),
      exercise_group_id: exerciseSet.exerciseGroupId || randomExerciseGroupId(),
      rep_text: rt === '' ? null : rt,
      weight_kg: weightKg,
      rep,
      duration_seconds: durationSeconds,
    };
  }

  // ============================================================================
  // WORKOUT METHODS
  // ============================================================================

  /**
   * Get lightweight workout list (no blocks/sets). Use for Library list; call getWorkoutById when opening one.
   *
   * Derives Focus + Equipment tags (Top-3 by exercise count, ties broken alphabetically) so the Library
   * Workouts table can render them without a per-row round trip. Source: exercise.muscle_group / equipment.
   */
  static async getWorkoutsSummary(): Promise<WorkoutSummary[]> {
    try {
      const { data: workouts, error: workoutsError } = await supabase
        .from('workout')
        .select('id, name, note, estimated_duration_minutes, difficulty, banner_image_url, is_public, created_by, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (workoutsError) {
        console.error('Error fetching workouts:', workoutsError);
        throw workoutsError;
      }
      return WorkoutService.buildWorkoutSummariesFromRows(workouts ?? []);
    } catch (error) {
      console.error('Error in getWorkoutsSummary:', error);
      throw error;
    }
  }

  /** Summary cards for specific workouts only (plan view, draft restore). Avoids loading the full library. */
  static async getWorkoutsSummaryByIds(workoutIds: string[]): Promise<WorkoutSummary[]> {
    const ids = Array.from(new Set(workoutIds.filter(Boolean)));
    if (!ids.length) return [];
    try {
      const { data: workouts, error } = await supabase
        .from('workout')
        .select('id, name, note, estimated_duration_minutes, difficulty, banner_image_url, is_public, created_by, created_at, updated_at')
        .in('id', ids);
      if (error) throw error;
      return WorkoutService.buildWorkoutSummariesFromRows(workouts ?? []);
    } catch (error) {
      console.error('Error in getWorkoutsSummaryByIds:', error);
      throw error;
    }
  }

  private static async buildWorkoutSummariesFromRows(
    workouts: Array<{
      id: string;
      name: string;
      note: string | null;
      estimated_duration_minutes: number | null;
      difficulty: string | null;
      banner_image_url: string | null;
      is_public: boolean;
      created_by: string | null;
      created_at: string;
      updated_at: string | null;
    }>,
  ): Promise<WorkoutSummary[]> {
    if (!workouts.length) return [];

    const workoutIds = workouts.map((w) => w.id);
    const { data: junctions, error: junctionError } = await supabase
      .from('blocks_in_workout')
      .select('workout_id, block_id')
      .in('workout_id', workoutIds);
    if (junctionError) {
      console.error('Error fetching blocks_in_workout:', junctionError);
      throw junctionError;
    }
    const blockIds = Array.from(new Set((junctions || []).map((j: { block_id: string }) => j.block_id)));

    type EibRow = {
      block_id: string;
      exercise_id: string | null;
      exercise: { muscle_group?: string | null; equipment?: string | null } | null;
    };
    const distinctExercisesByBlockId: Record<string, Set<string>> = {};
    const blockMuscles: Record<string, Map<string, number>> = {};
    const blockEquipment: Record<string, Map<string, number>> = {};
    if (blockIds.length > 0) {
      const { data: setRows, error: setsError } = await supabase
        .from('exercises_in_block')
        .select('block_id, exercise_id, exercise:exercise_id ( muscle_group, equipment )')
        .in('block_id', blockIds);
      if (!setsError && setRows) {
        const countedPairs = new Set<string>();
        (setRows as unknown as EibRow[]).forEach((r) => {
          if (!r.exercise_id) return;
          if (!distinctExercisesByBlockId[r.block_id]) distinctExercisesByBlockId[r.block_id] = new Set();
          distinctExercisesByBlockId[r.block_id].add(r.exercise_id);

          const pairKey = `${r.block_id}|${r.exercise_id}`;
          if (countedPairs.has(pairKey)) return;
          countedPairs.add(pairKey);

          const muscle = normalizeTagValue(r.exercise?.muscle_group);
          if (muscle) {
            if (!blockMuscles[r.block_id]) blockMuscles[r.block_id] = new Map();
            blockMuscles[r.block_id].set(muscle, (blockMuscles[r.block_id].get(muscle) || 0) + 1);
          }
          const equipment = normalizeTagValue(r.exercise?.equipment);
          if (equipment) {
            if (!blockEquipment[r.block_id]) blockEquipment[r.block_id] = new Map();
            blockEquipment[r.block_id].set(equipment, (blockEquipment[r.block_id].get(equipment) || 0) + 1);
          }
        });
      }
    }

    const idsByWorkout: Record<string, Set<string>> = {};
    const muscleCountsByWorkout: Record<string, Map<string, number>> = {};
    const equipmentCountsByWorkout: Record<string, Map<string, number>> = {};
    (junctions || []).forEach((j: { workout_id: string; block_id: string }) => {
      const exSet = distinctExercisesByBlockId[j.block_id];
      if (exSet) {
        if (!idsByWorkout[j.workout_id]) idsByWorkout[j.workout_id] = new Set();
        exSet.forEach((eid) => idsByWorkout[j.workout_id].add(eid));
      }
      const mMap = blockMuscles[j.block_id];
      if (mMap) {
        if (!muscleCountsByWorkout[j.workout_id]) muscleCountsByWorkout[j.workout_id] = new Map();
        const target = muscleCountsByWorkout[j.workout_id];
        mMap.forEach((count, key) => target.set(key, (target.get(key) || 0) + count));
      }
      const eMap = blockEquipment[j.block_id];
      if (eMap) {
        if (!equipmentCountsByWorkout[j.workout_id]) equipmentCountsByWorkout[j.workout_id] = new Map();
        const target = equipmentCountsByWorkout[j.workout_id];
        eMap.forEach((count, key) => target.set(key, (target.get(key) || 0) + count));
      }
    });
    const exerciseCountByWorkoutId: Record<string, number> = {};
    Object.entries(idsByWorkout).forEach(([wid, idset]) => {
      exerciseCountByWorkoutId[wid] = idset.size;
    });

    return workouts.map((w) => ({
      id: w.id,
      name: w.name,
      note: w.note ?? undefined,
      estimatedDurationMinutes: w.estimated_duration_minutes ?? undefined,
      difficulty: w.difficulty ?? undefined,
      bannerImageUrl:
        w.banner_image_url && isAllowlistedWorkoutBannerUrl(w.banner_image_url)
          ? w.banner_image_url
          : undefined,
      createdBy: w.created_by,
      isPublic: !!w.is_public,
      createdAt: w.created_at,
      updatedAt: w.updated_at ?? w.created_at,
      exerciseCount: exerciseCountByWorkoutId[w.id] || 0,
      focusTags: tagsSortedByCount(muscleCountsByWorkout[w.id]),
      equipmentTags: tagsSortedByCount(equipmentCountsByWorkout[w.id]),
    })) as WorkoutSummary[];
  }

  /**
   * Get all workouts with their blocks and exercise sets (heavy; use getWorkoutsSummary for lists).
   */
  static async getWorkouts(): Promise<WorkoutWithBlocks[]> {
    try {
      const { data: workouts, error: workoutsError } = await supabase
        .from('workout')
        .select('*')
        .order('created_at', { ascending: false });

      if (workoutsError) {
        console.error('Error fetching workouts:', workoutsError);
        throw workoutsError;
      }

      if (!workouts || workouts.length === 0) {
        return [];
      }

      // Fetch blocks for each workout
      const workoutsWithBlocks = await Promise.all(
        workouts.map(async (workout) => {
          const blocks = await this.getWorkoutBlocks(workout.id);
          return {
            ...workout,
            id: workout.id,
            name: workout.name,
            note: workout.note, // description → note
            estimatedDurationMinutes: workout.estimated_duration_minutes,
            difficulty: workout.difficulty,
            createdBy: workout.created_by,
            isPublic: workout.is_public,
            createdAt: workout.created_at,
            updatedAt: workout.updated_at,
            bannerImageUrl: (workout as { banner_image_url?: string }).banner_image_url || undefined,
            workoutBlocks: blocks,
          } as WorkoutWithBlocks;
        })
      );

      return workoutsWithBlocks;
    } catch (error) {
      console.error('Error in getWorkouts:', error);
      throw error;
    }
  }

  /**
   * Get workout by ID with blocks and exercise sets (short-lived cache to reduce duplicate fetches).
   */
  static async getWorkoutById(id: string): Promise<WorkoutWithBlocks | null> {
    const cached = WorkoutService.workoutByIdCache.get(id);
    if (cached && Date.now() - cached.timestamp < WORKOUT_BY_ID_CACHE_TTL_MS) {
      return cached.data;
    }
    try {
      const { data: workout, error: workoutError } = await supabase
        .from('workout')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (workoutError) {
        console.error('❌ Error fetching workout:', {
          code: workoutError.code,
          message: workoutError.message,
          details: workoutError.details,
          hint: workoutError.hint
        });
        // Don't throw if it's just "no rows" error, return null instead
        if (workoutError.code === 'PGRST116') {
          console.warn('⚠️ PGRST116: No rows returned (workout not found or RLS blocking)');
          return null;
        }
        // Check for RLS errors
        if (workoutError.code === '42501' || workoutError.message?.includes('permission') || workoutError.message?.includes('policy')) {
          console.error('🚫 RLS (Row Level Security) error detected!');
          console.error('💡 The workout may exist but RLS is blocking access.');
          console.error('💡 Solution: Make the workout public or update RLS policies.');
        }
        throw workoutError;
      }

      if (!workout) return null;

      const blocks = await this.getWorkoutBlocks(id);

      const result = {
        ...workout,
        id: workout.id,
        name: workout.name,
        note: workout.note, // description → note
        estimatedDurationMinutes: workout.estimated_duration_minutes,
        difficulty: workout.difficulty,
        createdBy: workout.created_by,
        isPublic: workout.is_public,
        createdAt: workout.created_at,
        updatedAt: workout.updated_at,
        bannerImageUrl: (workout as { banner_image_url?: string }).banner_image_url || undefined,
        workoutBlocks: blocks,
      } as WorkoutWithBlocks;
      WorkoutService.workoutByIdCache.set(id, { data: result, timestamp: Date.now() });
      return result;
    } catch (error) {
      console.error('Error in getWorkoutById:', error);
      throw error;
    }
  }

  /**
   * Get workout blocks for a workout
   * NEW SCHEMA: Blocks linked via blocks_in_workout junction table
   */
  static async getWorkoutBlocks(workoutId: string): Promise<(WorkoutBlock & { exerciseSets: (ExerciseSet & { exercise: any })[] })[]> {
    try {
      // First get block IDs from junction table
      const { data: junctionData, error: junctionError } = await supabase
        .from('blocks_in_workout')
        .select('block_id, order')
        .eq('workout_id', workoutId)
        .order('order', { ascending: true });

      if (junctionError) {
        console.error('Error fetching blocks_in_workout:', junctionError);
        throw junctionError;
      }

      if (!junctionData || junctionData.length === 0) {
        return [];
      }

      // Get blocks by IDs
      const blockIds = junctionData.map(j => j.block_id);
      const { data: blocks, error: blocksError } = await supabase
        .from('block')
        .select('*')
        .in('id', blockIds);

      if (blocksError) {
        console.error('Error fetching workout blocks:', blocksError);
        throw blocksError;
      }

      if (!blocks || blocks.length === 0) {
        return [];
      }

      // Create a map of block_id -> order for fast lookup
      const orderMap = new Map(junctionData.map(j => [j.block_id, j.order]));

      // Sort blocks by order from junction table (preserve the order from blocks_in_workout)
      const sortedBlocks = blocks.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? 0;
        const orderB = orderMap.get(b.id) ?? 0;
        return orderA - orderB;
      });

      // Fetch all exercise sets for these blocks in one query
      const setsByBlockId = await this.getExerciseSetsForBlocks(blockIds);

      const blocksWithSets = sortedBlocks.map((block) => {
        const junctionEntry = junctionData.find(j => j.block_id === block.id);
        const exerciseSets = setsByBlockId[block.id] ?? [];
        return {
          id: block.id,
          sets: block.sets,
          type: block.type,
          name: block.name,
          order: junctionEntry?.order || 0,
          restSeconds: block.rest_seconds,
          note: block.note,
          createdBy: block.created_by,
          createdAt: block.created_at,
          updatedAt: block.updated_at,
          exerciseSets,
        };
      });

      return blocksWithSets;
    } catch (error) {
      console.error('Error in getWorkoutBlocks:', error);
      throw error;
    }
  }

  /**
   * Get exercise sets for multiple blocks in one query (batch).
   */
  private static async getExerciseSetsForBlocks(blockIds: string[]): Promise<Record<string, (ExerciseSet & { exercise: any })[]>> {
    if (blockIds.length === 0) return {};
    try {
      const { data: sets, error: setsError } = await supabase
        .from('exercises_in_block')
        .select(`
          *,
          exercise (*)
        `)
        .in('block_id', blockIds)
        .order('block_id', { ascending: true })
        .order('order', { ascending: true });

      if (setsError) {
        console.error('Error fetching exercise sets for blocks:', setsError);
        throw setsError;
      }

      const byBlockId: Record<string, (ExerciseSet & { exercise: any })[]> = {};
      for (const id of blockIds) byBlockId[id] = [];
      if (!sets || sets.length === 0) return byBlockId;

      const uniqueExerciseIds = Array.from(new Set(sets.map((s: any) => s.exercise_id).filter(Boolean)));
      const { data: { user } } = await supabase.auth.getUser();
      const exerciseMap = await ExerciseService.getExercisesByIds(uniqueExerciseIds, user?.id);

      const rawRowToExercise = (row: any): any => {
        if (!row) return undefined;
        return {
          id: row.id,
          name: row.name,
          muscleGroup: row.muscle_group ?? undefined,
          equipment: row.equipment ?? undefined,
          description: row.description,
          steps: getExecutionStepsForDisplay(
            row.description,
            Array.isArray(row.steps) ? row.steps : [],
          ),
          image: row.image,
          video: row.video,
          isPublic: row.is_public,
          createdBy: row.created_by,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        };
      };

      for (const set of sets) {
        const bid = set.block_id;
        if (!byBlockId[bid]) byBlockId[bid] = [];
        const exerciseResolved = exerciseMap[set.exercise_id] ?? rawRowToExercise(set.exercise);
        byBlockId[bid].push(
          WorkoutService.mapEibRowToExerciseSet(set, exerciseResolved) as ExerciseSet & { exercise: any }
        );
      }
      return byBlockId;
    } catch (error) {
      console.error('Error in getExerciseSetsForBlocks:', error);
      throw error;
    }
  }

  /**
   * Get exercise sets for a workout block
   * NEW SCHEMA: exercises_in_block table, exercise table
   */
  static async getExerciseSets(blockId: string): Promise<(ExerciseSet & { exercise: any })[]> {
    try {
      const { data: sets, error: setsError } = await supabase
        .from('exercises_in_block')
        .select(`
          *,
          exercise (*)
        `)
        .eq('block_id', blockId)
        .order('order', { ascending: true });

      if (setsError) {
        console.error('Error fetching exercise sets:', setsError);
        throw setsError;
      }

      if (!sets || sets.length === 0) {
        return [];
      }

      return sets.map((set) =>
        WorkoutService.mapEibRowToExerciseSet(set, set.exercise)
      ) as (ExerciseSet & { exercise: any })[];
    } catch (error) {
      console.error('Error in getExerciseSets:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // Exercise snapshots (member sees exercise as it was when assigned)
  // --------------------------------------------------------------------------

  /** Snapshot row from DB */
  private static async getSessionExerciseSnapshots(sessionId: string): Promise<Array<{
    workout_session_id: string;
    block_id: string;
    exercise_id: string;
    order: number;
    name: string | null;
    muscle_group: string | null;
    equipment: string | null;
    description: string | null;
    image: string | null;
    video: string | null;
    is_public: boolean | null;
  }>> {
    const { data, error } = await supabase
      .from('workout_exercise_snapshot')
      .select('*')
      .eq('workout_session_id', sessionId);
    if (error) {
      console.warn('Error fetching exercise snapshots for session:', sessionId, error);
      return [];
    }
    return data ?? [];
  }

  /** Batch fetch snapshots for many sessions (one query instead of N). Returns map of sessionId -> snapshot rows. */
  private static async getSnapshotsBySessionIds(sessionIds: string[]): Promise<Map<string, Array<{
    workout_session_id: string;
    block_id: string;
    exercise_id: string;
    order: number;
    name: string | null;
    muscle_group: string | null;
    equipment: string | null;
    description: string | null;
    image: string | null;
    video: string | null;
    is_public: boolean | null;
  }>>> {
    if (sessionIds.length === 0) return new Map();
    const { data, error } = await supabase
      .from('workout_exercise_snapshot')
      .select('*')
      .in('workout_session_id', sessionIds);
    if (error) {
      console.warn('Error batch fetching exercise snapshots:', error);
      return new Map();
    }
    const map = new Map<string, typeof data>();
    for (const row of data ?? []) {
      const sid = row.workout_session_id;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid)!.push(row);
    }
    return map;
  }

  /** Build an Exercise-like object from a snapshot row for display */
  private static snapshotToExercise(snapshot: {
    exercise_id: string;
    name: string | null;
    muscle_group: string | null;
    equipment: string | null;
    description: string | null;
    steps?: string[] | null;
    image: string | null;
    video: string | null;
    is_public: boolean | null;
  }): { id: string; name: string; muscleGroup?: string; equipment?: string; description?: string; steps?: string[]; image?: string; video?: string; isPublic: boolean; createdAt: string; updatedAt: string } {
    return {
      id: snapshot.exercise_id,
      name: snapshot.name ?? '',
      muscleGroup: snapshot.muscle_group ?? undefined,
      equipment: snapshot.equipment ?? undefined,
      description: snapshot.description ?? undefined,
      steps: getExecutionStepsForDisplay(snapshot.description, snapshot.steps),
      image: snapshot.image ?? undefined,
      video: snapshot.video ?? undefined,
      isPublic: snapshot.is_public ?? false,
      createdAt: '',
      updatedAt: '',
    };
  }

  /** Merge snapshots into workout so member sees exercise at assignment time */
  private static applySnapshotsToWorkout(
    workout: WorkoutWithBlocks,
    snapshots: Array<{ block_id: string; exercise_id: string; order?: number; [k: string]: any }>
  ): void {
    if (!workout.workoutBlocks || snapshots.length === 0) return;
    const orderVal = (s: { order?: number; [k: string]: any }) => s.order ?? (s as any)['order'] ?? 0;
    const key = (b: string, e: string, o: number | string) => `${b}:${e}:${o}`;
    const keyExOrder = (e: string, o: number | string) => `${e}:${o}`;
    const map = new Map(snapshots.map(s => [key(s.block_id, s.exercise_id, orderVal(s)), s]));
    const mapByExOrder = new Map(snapshots.map(s => [keyExOrder(s.exercise_id, orderVal(s)), s]));
    // Fallback: single snapshot per exercise_id (when only one snapshot for this exercise in session)
    const byExercise = new Map<string, typeof snapshots[0] | null>();
    for (const s of snapshots) {
      if (!byExercise.has(s.exercise_id)) byExercise.set(s.exercise_id, s);
      else byExercise.set(s.exercise_id, null); // multiple snapshots for same exercise = ambiguous
    }
    for (const block of workout.workoutBlocks) {
      for (const set of block.exerciseSets ?? []) {
        const setOrder = set.order ?? (set as any).order ?? 0;
        let s = map.get(key(block.id, set.exerciseId, setOrder))
          ?? mapByExOrder.get(keyExOrder(set.exerciseId, setOrder))
          ?? (byExercise.get(set.exerciseId) ?? null);
        if (s) {
          (set as ExerciseSet & { exercise?: any }).exercise = this.snapshotToExercise(s as unknown as {
            exercise_id: string;
            name: string | null;
            muscle_group: string | null;
            equipment: string | null;
            description: string | null;
            image: string | null;
            video: string | null;
            is_public: boolean | null;
          });
        }
      }
    }
  }

  /** Create snapshot rows for a session so member sees trainer's version at assignment */
  private static async createSnapshotsForSession(
    sessionId: string,
    workoutId: string,
    cachedWorkout?: WorkoutWithBlocks | null,
  ): Promise<void> {
    const workout = cachedWorkout ?? (await this.getWorkoutById(workoutId));
    if (!workout?.workoutBlocks) return;

    const exerciseIds = new Set<string>();
    for (const block of workout.workoutBlocks) {
      for (const set of block.exerciseSets ?? []) {
        if (set.exerciseId) exerciseIds.add(set.exerciseId);
      }
    }
    if (exerciseIds.size === 0) return;

    const { data: { user } } = await supabase.auth.getUser();
    const exerciseMap = await ExerciseService.getExercisesByIds(Array.from(exerciseIds), user?.id);

    const rows: Array<{
      workout_session_id: string;
      block_id: string;
      exercise_id: string;
      order: number;
      name: string | null;
      muscle_group: string | null;
      equipment: string | null;
      description: string | null;
      image: string | null;
      video: string | null;
      is_public: boolean | null;
    }> = [];

    for (const block of workout.workoutBlocks) {
      for (const set of block.exerciseSets ?? []) {
        const exercise = exerciseMap[set.exerciseId];
        if (!exercise) continue;
        const executionSteps = getExecutionStepsForDisplay(exercise.description, exercise.steps);
        const description =
          executionSteps.length > 0
            ? stepsToDescription(executionSteps)
            : (exercise.description ?? null);
        rows.push({
          workout_session_id: sessionId,
          block_id: block.id,
          exercise_id: set.exerciseId,
          order: set.order,
          name: exercise.name ?? null,
          muscle_group: exercise.muscleGroup ?? null,
          equipment: exercise.equipment ?? null,
          description,
          image: exercise.image ?? null,
          video: exercise.video ?? null,
          is_public: exercise.isPublic ?? null,
        });
      }
    }
    if (rows.length === 0) return;
    const { error } = await supabase.from('workout_exercise_snapshot').insert(rows);
    if (error) {
      console.warn('Error inserting exercise snapshots for session:', sessionId, error);
    }
  }

  /**
   * Check if an exercise is used in upcoming workouts of members (plan not completed).
   * Returns session IDs and member count for the warning modal.
   */
  static async getUpcomingSessionsUsingExercise(exerciseId: string): Promise<{ sessionIds: string[]; memberCount: number }> {
    // 1. Workout IDs that contain this exercise (via exercises_in_block -> blocks_in_workout)
    const { data: eib } = await supabase
      .from('exercises_in_block')
      .select('block_id')
      .eq('exercise_id', exerciseId);
    if (!eib?.length) return { sessionIds: [], memberCount: 0 };
    const blockIds = Array.from(new Set(eib.map((r) => r.block_id)));

    const { data: biw } = await supabase
      .from('blocks_in_workout')
      .select('workout_id')
      .in('block_id', blockIds);
    if (!biw?.length) return { sessionIds: [], memberCount: 0 };
    const workoutIds = Array.from(new Set(biw.map((r) => r.workout_id)));

    // 2. Upcoming sessions (scheduled/in_progress) for those workouts
    const { data: sessions, error } = await supabase
      .from('workout_session')
      .select('id, user_id, users_workoutplan_id')
      .in('workout_id', workoutIds)
      .in('status', ['scheduled', 'in_progress']);

    if (error || !sessions?.length) return { sessionIds: [], memberCount: 0 };

    // 3. Filter: direct assignment (users_workoutplan_id null) or assignment status active/paused
    const planIds = sessions.map((s) => s.users_workoutplan_id).filter(Boolean) as string[];
    let validPlanIds = new Set<string>();
    if (planIds.length > 0) {
      const { data: plans } = await supabase
        .from('users_workoutplan')
        .select('id')
        .in('id', planIds)
        .in('status', ['active', 'paused']);
      validPlanIds = new Set((plans ?? []).map((p) => p.id));
    }

    const validSessions = sessions.filter(
      (s) => s.users_workoutplan_id == null || validPlanIds.has(s.users_workoutplan_id)
    );
    const sessionIds = validSessions.map((s) => s.id);
    const memberCount = new Set(validSessions.map((s) => s.user_id)).size;
    return { sessionIds, memberCount };
  }

  /**
   * Update snapshots for upcoming sessions using this exercise.
   * Call after trainer chooses "Yes, update their workouts" in the edit warning modal.
   * Uses upsert so that sessions without snapshots (e.g. assigned before the feature) get them created.
   */
  static async updateSnapshotsForUpcomingSessions(
    exerciseId: string,
    exerciseData: { name?: string; muscleGroup?: string; equipment?: string; description?: string; image?: string; video?: string; isPublic?: boolean }
  ): Promise<void> {
    const { sessionIds } = await this.getUpcomingSessionsUsingExercise(exerciseId);
    if (sessionIds.length === 0) return;

    const snapshotData = {
      name: exerciseData.name ?? null,
      muscle_group: exerciseData.muscleGroup ?? null,
      equipment: exerciseData.equipment ?? null,
      description: exerciseData.description ?? null,
      image: exerciseData.image ?? null,
      video: exerciseData.video ?? null,
      is_public: exerciseData.isPublic ?? null,
    };

    // Build rows for upsert: one per (session, block, exercise, order)
    const rows: Array<{
      workout_session_id: string;
      block_id: string;
      exercise_id: string;
      order: number;
      name: string | null;
      muscle_group: string | null;
      equipment: string | null;
      description: string | null;
      image: string | null;
      video: string | null;
      is_public: boolean | null;
    }> = [];

    for (const sessionId of sessionIds) {
      const { data: session } = await supabase
        .from('workout_session')
        .select('workout_id')
        .eq('id', sessionId)
        .maybeSingle();
      if (!session?.workout_id) continue;

      const workout = await this.getWorkoutById(session.workout_id);
      if (!workout?.workoutBlocks) continue;

      for (const block of workout.workoutBlocks) {
        for (const set of block.exerciseSets ?? []) {
          if (set.exerciseId !== exerciseId) continue;
          rows.push({
            workout_session_id: sessionId,
            block_id: block.id,
            exercise_id: exerciseId,
            order: set.order,
            ...snapshotData,
          });
        }
      }
    }

    if (rows.length === 0) return;

    const { error } = await supabase
      .from('workout_exercise_snapshot')
      .upsert(rows, {
        onConflict: 'workout_session_id,block_id,exercise_id,order',
        ignoreDuplicates: false,
      });

    if (error) {
      console.warn('Error upserting exercise snapshots for upcoming sessions:', error);
      throw error;
    }
  }

  /**
   * Create a new workout with blocks and exercise sets
   */
  static async createWorkout(workoutData: CreateWorkoutData): Promise<WorkoutWithBlocks> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const bannerUrl = (workoutData.bannerImageUrl || '').trim();
      if (bannerUrl && !isAllowlistedWorkoutBannerUrl(bannerUrl)) {
        throw new Error('Workout cover image must be a gallery image from the app.');
      }

      // Create the workout
      const { data: workout, error: workoutError } = await supabase
        .from('workout')
        .insert({
          name: workoutData.name,
          note: workoutData.note, // description → note
          estimated_duration_minutes: workoutData.estimatedDurationMinutes,
          difficulty: workoutData.difficulty,
          banner_image_url: bannerUrl || null,
          is_public: workoutData.isPublic,
          created_by: user.id,
        })
        .select()
        .single();

      if (workoutError) {
        console.error('Error creating workout:', workoutError);
        throw workoutError;
      }

      // Note: Blocks and exercise sets should be added separately after workout creation
      // This allows for a better UX where users can add blocks incrementally

      // Return the complete workout
      const completeWorkout = await this.getWorkoutById(workout.id);
      if (!completeWorkout) {
        throw new Error('Failed to fetch created workout');
      }

      return completeWorkout;
    } catch (error) {
      console.error('Error in createWorkout:', error);
      throw error;
    }
  }

  /**
   * Atomic trainer-library workout duplicate (`duplicate_trainer_workout`).
   * @see `_bmad-output/planning-artifacts/workout-duplicate-rpc-and-ui-spec.md`
   */
  static async duplicateTrainerWorkout(sourceWorkoutId: string): Promise<DuplicateTrainerWorkoutRpcResult> {
    const t0 =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const { data, error } = await supabase.rpc('duplicate_trainer_workout', {
      p_source_workout_id: sourceWorkoutId,
      p_target_name: null,
    });
    const durationMs =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? Math.round(performance.now() - t0)
        : null;
    if (error) {
      console.error('[duplicateTrainerWorkout] rpc error:', error);
      throw error;
    }
    const parsed = parseDuplicateTrainerWorkoutRpcPayload(data);
    console.info('[duplicateTrainerWorkout]', {
      durationMs,
      source_workout_id: parsed.source_workout_id,
      new_workout_id: parsed.new_workout_id,
      blocks_copied: parsed.blocks_copied,
      exercise_set_rows_copied: parsed.exercise_set_rows_copied,
      distinct_exercise_count: parsed.distinct_exercise_count,
    });
    this.invalidateWorkoutCache(sourceWorkoutId);
    this.invalidateWorkoutCache(parsed.new_workout_id);
    return parsed;
  }

  /**
   * Legacy REST orchestration (pre-RPC). Used when `upsert_trainer_workout` is not deployed.
   */
  private static async updateWorkoutViaRestOrchestration(
    workoutId: string,
    workoutData: WorkoutWithBlocks,
    userId: string,
  ): Promise<WorkoutWithBlocks> {
    const bannerUrl = (workoutData.bannerImageUrl || '').trim();

    const { error: workoutError } = await supabase
      .from('workout')
      .update({
        name: workoutData.name,
        note: workoutData.note,
        estimated_duration_minutes: workoutData.estimatedDurationMinutes,
        difficulty: workoutData.difficulty,
        is_public: workoutData.isPublic,
        banner_image_url: bannerUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', workoutId);

    if (workoutError) {
      console.error('Error updating workout:', workoutError);
      throw workoutError;
    }

    const { data: existingJunctions, error: junctionError } = await supabase
      .from('blocks_in_workout')
      .select('block_id, order')
      .eq('workout_id', workoutId)
      .order('order', { ascending: true });

    if (junctionError) {
      console.error('Error fetching existing blocks:', junctionError);
      throw junctionError;
    }

    const existingBlockIds = new Set(existingJunctions?.map((j) => j.block_id) || []);
    const newBlockIdByIndex: (string | undefined)[] = [];

    if (workoutData.workoutBlocks && workoutData.workoutBlocks.length > 0) {
      for (let blockIndex = 0; blockIndex < workoutData.workoutBlocks.length; blockIndex++) {
        const block = workoutData.workoutBlocks[blockIndex];
        const blockOrder = blockIndex + 1;

        let blockId = block.id;
        const isNewBlock = !blockId || blockId.startsWith('temp-') || !existingBlockIds.has(blockId);

        const blockNote =
          block.note !== undefined &&
          block.note !== null &&
          typeof block.note === 'string' &&
          block.note.trim() !== ''
            ? block.note.trim()
            : null;

        if (isNewBlock) {
          const { data: newBlockData, error: blockError } = await supabase
            .from('block')
            .insert({
              sets: block.sets || 1,
              type: block.type,
              name: block.name,
              rest_seconds: block.restSeconds,
              note: blockNote,
              created_by: userId,
            })
            .select()
            .single();

          if (blockError) {
            console.error('Error creating block:', blockError);
            throw blockError;
          }

          blockId = newBlockData.id;
          newBlockIdByIndex[blockIndex] = blockId;

          const { error: linkError } = await supabase.from('blocks_in_workout').insert({
            workout_id: workoutId,
            block_id: blockId,
            order: blockOrder,
          });

          if (linkError) {
            console.error('Error linking block to workout:', linkError);
            throw linkError;
          }
        } else {
          const updatePayload = {
            sets: block.sets || 1,
            type: block.type,
            name: block.name,
            rest_seconds: block.restSeconds,
            note: blockNote,
            updated_at: new Date().toISOString(),
          };
          const { data: updateResult, error: updateBlockError } = await supabase
            .from('block')
            .update(updatePayload)
            .eq('id', blockId)
            .select();
          if (updateBlockError) {
            console.error('Error updating block:', updateBlockError);
            throw updateBlockError;
          }
          if (!updateResult || updateResult.length === 0) {
            throw new Error(`Update failed: No rows affected for block ${blockId}. Possible RLS issue.`);
          }

          const existingJunction = existingJunctions?.find((j) => j.block_id === blockId);
          if (!existingJunction || existingJunction.order !== blockOrder) {
            const { error: updateOrderError } = await supabase
              .from('blocks_in_workout')
              .update({ order: blockOrder })
              .eq('workout_id', workoutId)
              .eq('block_id', blockId);

            if (updateOrderError) {
              console.error('Error updating block order:', updateOrderError);
              throw updateOrderError;
            }
          }

          existingBlockIds.delete(blockId);
        }

        const { data: existingSets, error: setsError } = await supabase
          .from('exercises_in_block')
          .select('id, order')
          .eq('block_id', blockId)
          .order('order', { ascending: true });

        if (setsError) {
          console.error('Error fetching existing exercise sets:', setsError);
          throw setsError;
        }

        const existingSetIds = new Set(existingSets?.map((s) => s.id) || []);
        const TEMP_ORDER_BASE = 1_000_000;
        if (existingSets && existingSets.length > 0) {
          const stageResults = await Promise.all(
            existingSets.map((row, i) =>
              supabase
                .from('exercises_in_block')
                .update({ order: TEMP_ORDER_BASE + i, updated_at: new Date().toISOString() })
                .eq('id', row.id),
            ),
          );
          const stageErr = stageResults.find((r) => r.error)?.error;
          if (stageErr) {
            console.error('Error staging exercise set order:', stageErr);
            throw stageErr;
          }
        }

        if (block.exerciseSets && block.exerciseSets.length > 0) {
          const setUpdatePromises: PromiseLike<{ error: unknown }>[] = [];
          for (let setIndex = 0; setIndex < block.exerciseSets.length; setIndex++) {
            const exerciseSet = block.exerciseSets[setIndex];
            const setOrder = setIndex + 1;
            if (!exerciseSet.exerciseId) continue;

            const isNewSet =
              !exerciseSet.id || exerciseSet.id.startsWith('temp-') || !existingSetIds.has(exerciseSet.id);

            if (isNewSet) {
              const insertPayload = WorkoutService.eibPayloadFromExerciseSet(exerciseSet, setOrder, blockId);
              const { error: createSetError } = await supabase.from('exercises_in_block').insert(insertPayload);
              if (createSetError) {
                console.error('Error creating exercise set:', createSetError);
                throw createSetError;
              }
            } else {
              const updateData = WorkoutService.eibUpdatePayloadFromExerciseSet(exerciseSet, setOrder);
              const setId = exerciseSet.id;
              setUpdatePromises.push(supabase.from('exercises_in_block').update(updateData).eq('id', setId));
              existingSetIds.delete(setId);
            }
          }
          if (setUpdatePromises.length > 0) {
            const updateResults = await Promise.all(setUpdatePromises);
            const updateSetError = updateResults.find((r) => r.error)?.error;
            if (updateSetError) {
              console.error('Error updating exercise set:', updateSetError);
              throw updateSetError;
            }
          }
        }

        if (existingSetIds.size > 0) {
          const { error: deleteSetsError } = await supabase
            .from('exercises_in_block')
            .delete()
            .in('id', Array.from(existingSetIds));

          if (deleteSetsError) {
            console.error('Error deleting exercise sets:', deleteSetsError);
            throw deleteSetsError;
          }
        }
      }
    }

    if (existingBlockIds.size > 0) {
      const { error: deleteJunctionsError } = await supabase
        .from('blocks_in_workout')
        .delete()
        .eq('workout_id', workoutId)
        .in('block_id', Array.from(existingBlockIds));

      if (deleteJunctionsError) {
        console.error('Error deleting block junctions:', deleteJunctionsError);
        throw deleteJunctionsError;
      }

      const { error: deleteBlocksError } = await supabase
        .from('block')
        .delete()
        .in('id', Array.from(existingBlockIds));

      if (deleteBlocksError) {
        console.error('Error deleting blocks:', deleteBlocksError);
        console.warn('Some blocks may be in use by other workouts');
      }
    }

    WorkoutService.workoutByIdCache.delete(workoutId);
    const patchedBlocks = (workoutData.workoutBlocks || []).map((b, i) => ({
      ...b,
      id: newBlockIdByIndex[i] ?? b.id,
    }));
    return {
      ...workoutData,
      id: workoutId,
      workoutBlocks: patchedBlocks,
    } as WorkoutWithBlocks;
  }

  private static async updateWorkoutViaRpc(
    workoutId: string,
    workoutData: WorkoutWithBlocks,
    started: number,
  ): Promise<WorkoutWithBlocks> {
    const blockCount = workoutData.workoutBlocks?.length ?? 0;
    const setCount = (workoutData.workoutBlocks ?? []).reduce(
      (n, b) => n + (b.exerciseSets?.filter((s) => s.exerciseId)?.length ?? 0),
      0,
    );

    const { data, error } = await supabase.rpc('upsert_trainer_workout', {
      p_workout_id: workoutId,
      p_payload: buildUpsertTrainerWorkoutPayload(workoutData),
    });

    if (error) {
      console.error('upsert_trainer_workout RPC error:', error);
      throw error;
    }

    const parsed = parseUpsertTrainerWorkoutRpcPayload(data);
    WorkoutService.workoutByIdCache.delete(workoutId);

    if (process.env.NODE_ENV === 'development') {
      const durationMs = Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started,
      );
      console.info('[updateWorkout]', {
        blocks: blockCount,
        sets: setCount,
        durationMs,
        usedRpc: true,
        blocks_written: parsed.blocks_written,
        exercise_set_rows_written: parsed.exercise_set_rows_written,
      });
    }

    return patchWorkoutIdsFromPayload(workoutData, workoutId, parsed);
  }

  /**
   * Update a workout with its blocks and exercise sets (`upsert_trainer_workout` RPC, REST fallback if missing).
   */
  static async updateWorkout(workoutId: string, workoutData: WorkoutWithBlocks): Promise<WorkoutWithBlocks> {
    const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const bannerUrl = (workoutData.bannerImageUrl || '').trim();
      if (!isAllowlistedWorkoutBannerUrl(bannerUrl)) {
        throw new Error('Workout cover image must be a gallery image from the app.');
      }

      const forceRest = process.env.REACT_APP_USE_UPSERT_WORKOUT_RPC === 'false';
      if (forceRest) {
        const result = await WorkoutService.updateWorkoutViaRestOrchestration(
          workoutId,
          workoutData,
          user.id,
        );
        if (process.env.NODE_ENV === 'development') {
          console.info('[updateWorkout]', { usedRpc: false, reason: 'REACT_APP_USE_UPSERT_WORKOUT_RPC=false' });
        }
        return result;
      }

      try {
        return await WorkoutService.updateWorkoutViaRpc(workoutId, workoutData, started);
      } catch (rpcError) {
        const rpcMsg = messageFromUnknownError(rpcError);
        const isTimeout =
          (typeof rpcError === 'object' &&
            rpcError !== null &&
            (rpcError as { code?: string }).code === '57014') ||
          /statement timeout/i.test(rpcMsg);
        if (!shouldFallbackUpsertTrainerWorkoutRpc(rpcError) && !isTimeout) {
          throw rpcError;
        }
        console.warn(
          '[updateWorkout] upsert_trainer_workout failed; falling back to REST orchestration',
          rpcMsg,
        );
        const result = await WorkoutService.updateWorkoutViaRestOrchestration(
          workoutId,
          workoutData,
          user.id,
        );
        if (process.env.NODE_ENV === 'development') {
          const durationMs = Math.round(
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started,
          );
          console.info('[updateWorkout]', { usedRpc: false, durationMs, reason: 'rpc_not_deployed' });
        }
        return result;
      }
    } catch (error) {
      console.error('Error in updateWorkout:', error);
      throw error;
    }
  }

  /**
   * Delete a workout
   * Note: Due to CASCADE constraints, this will also delete:
   * - All blocks_in_workout links
   * - All workouts_in_workoutplan links
   * - All workout_session records
   * The blocks and exercise sets themselves are not deleted (they can be reused)
   */
  static async deleteWorkout(workoutId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('workout')
        .delete()
        .eq('id', workoutId);

      if (error) {
        console.error('Error deleting workout:', error);
        throw error;
      }
      WorkoutService.workoutByIdCache.delete(workoutId);
    } catch (error) {
      console.error('Error in deleteWorkout:', error);
      throw error;
    }
  }

  /**
   * Create a workout block
   */
  static async createWorkoutBlock(
    workoutId: string,
    blockData: CreateWorkoutBlockData
  ): Promise<WorkoutBlock> {
    try {
      // Create the block (NEW SCHEMA: blocks are reusable, linked via blocks_in_workout)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: block, error: blockError } = await supabase
        .from('block')
        .insert({
          sets: blockData.sets, // NEW: Required
          type: blockData.type,
          name: blockData.name,
          rest_seconds: blockData.restSeconds, // restBetweenRoundsSeconds → restSeconds
          note: blockData.note, // notes → note
          created_by: user.id,
        })
        .select()
        .single();

      if (blockError) {
        console.error('Error creating workout block:', blockError);
        throw blockError;
      }

      // Link block to workout via junction table
      const { error: junctionError } = await supabase
        .from('blocks_in_workout')
        .insert({
          workout_id: workoutId,
          block_id: block.id,
          order: 0, // TODO: Calculate proper order
        });

      if (junctionError) {
        console.error('Error linking block to workout:', junctionError);
        throw junctionError;
      }

      // Note: Exercise sets should be added separately after block creation

      return {
        id: block.id,
        sets: block.sets,
        type: block.type,
        name: block.name,
        restSeconds: block.rest_seconds,
        note: block.note,
        createdBy: block.created_by,
        createdAt: block.created_at,
        updatedAt: block.updated_at,
      };
    } catch (error) {
      console.error('Error in createWorkoutBlock:', error);
      throw error;
    }
  }

  /**
   * Create an exercise set
   */
  static async createExerciseSet(
    workoutBlockId: string,
    exerciseSetData: CreateExerciseSetData
  ): Promise<ExerciseSet> {
    try {
      const insertData = WorkoutService.eibPayloadFromExerciseSet(
        exerciseSetData,
        exerciseSetData.order,
        exerciseSetData.blockId
      );

      const { data: exerciseSet, error: exerciseSetError } = await supabase
        .from('exercises_in_block')
        .insert(insertData)
        .select()
        .single();

      if (exerciseSetError) {
        console.error('Error creating exercise set:', exerciseSetError);
        throw exerciseSetError;
      }

      return WorkoutService.mapEibRowToExerciseSet(exerciseSet, undefined);
    } catch (error) {
      console.error('Error in createExerciseSet:', error);
      throw error;
    }
  }

  /**
   * Get exercise IDs that the trainer has used in at least minWorkouts different workouts
   * (for favorites star in exercise selector). Option A: star only if used in >= 2 workouts.
   */
  static async getTrainerFavoriteExerciseIds(
    trainerId: string,
    minWorkouts: number = 2
  ): Promise<string[]> {
    try {
      const { data, error } = await supabase.rpc('get_trainer_favorite_exercise_ids', {
        p_trainer_id: trainerId,
        p_min_workouts: minWorkouts,
      });
      if (error) {
        console.error('Error fetching trainer favorite exercise IDs:', error);
        return [];
      }
      return (data ?? []).map((id: string) => String(id));
    } catch (err) {
      console.error('Error in getTrainerFavoriteExerciseIds:', err);
      return [];
    }
  }

  // ============================================================================
  // WORKOUT PLAN METHODS
  // ============================================================================

  /**
   * Get all workout plans with their schedules
   * Only returns templates (trainer's library); instances are per-member and not shown in the library
   */
  static async getWorkoutPlans(): Promise<WorkoutPlanWithSchedule[]> {
    try {
      // Library Plans tab needs difficulty + total_no_of_days for client-side
      // Duration / Level chip filters and Sort options. Pull them in this single
      // round-trip rather than fanning out to per-row queries.
      // See _bmad-output/planning-artifacts/trainer-library-workout-plans-redesign-dev-handoff.md §6.
      const { data: plans, error: plansError } = await supabase
        .from('workoutplan')
        .select('id, name, description, total_no_of_days, difficulty, created_by, created_at')
        .eq('is_template', true)
        .order('created_at', { ascending: false });

      if (plansError) {
        console.error('Error fetching workout plans:', plansError);
        throw plansError;
      }

      if (!plans || plans.length === 0) {
        return [];
      }

      // Return plans without full schedule (lightweight). Use getWorkoutPlanById(planId) when full schedule is needed.
      return plans.map((plan) => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        totalNoOfDays: plan.total_no_of_days,
        difficulty: plan.difficulty,
        createdBy: plan.created_by,
        createdAt: plan.created_at,
        planWorkouts: [],
      })) as unknown as WorkoutPlanWithSchedule[];
    } catch (error) {
      console.error('Error in getWorkoutPlans:', error);
      throw error;
    }
  }

  /**
   * Get workout plan by ID with schedule
   */
  static async getWorkoutPlanById(id: string): Promise<WorkoutPlanWithSchedule | null> {
    try {
      const { data: plan, error: planError } = await supabase
        .from('workoutplan')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (planError) {
        console.error('Error fetching workout plan:', planError);
        throw planError;
      }

      if (!plan) return null;

      const planWorkouts = await this.getPlanWorkouts(id);

      return {
        ...plan,
        id: plan.id,
        name: plan.name,
        description: plan.description,
        totalNoOfDays: plan.total_no_of_days, // durationWeeks → totalNoOfDays
        difficulty: plan.difficulty,
        createdBy: plan.created_by, // createdByTrainerId → createdBy
        isPublic: plan.is_public,
        isTemplate: plan.is_template ?? true,
        templateId: plan.template_id,
        templateVersion: plan.template_version ?? 1,
        createdAt: plan.created_at,
        updatedAt: plan.updated_at,
        planWorkouts,
      } as WorkoutPlanWithSchedule;
    } catch (error) {
      console.error('Error in getWorkoutPlanById:', error);
      throw error;
    }
  }

  /**
   * Plan + schedule for the trainer plan builder/view page.
   * Loads workout summary cards only (no blocks/sets). Use getWorkoutPlanById when full graphs are required.
   */
  static async getWorkoutPlanViewById(id: string): Promise<WorkoutPlanWithSchedule | null> {
    try {
      const { data: plan, error: planError } = await supabase
        .from('workoutplan')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (planError) {
        console.error('Error fetching workout plan:', planError);
        throw planError;
      }
      if (!plan) return null;

      const { data: schedRows, error: schedError } = await fetchWorkoutsInPlanSchedule(
        id,
        'id, workoutplan_id, workout_id, day_no, order_in_day, created_at',
      );
      if (schedError) {
        console.error('Error fetching plan schedule:', schedError);
        throw schedError;
      }

      const uniqueWorkoutIds = Array.from(new Set(schedRows.map((r) => r.workout_id)));
      const summaries = await WorkoutService.getWorkoutsSummaryByIds(uniqueWorkoutIds);
      const summaryById = new Map(summaries.map((s) => [s.id, s]));

      const planWorkouts = schedRows.map((pw) => ({
        ...mapPlanScheduleRow(pw),
        workout: summaryById.get(pw.workout_id),
      }));

      return {
        ...plan,
        id: plan.id,
        name: plan.name,
        description: plan.description,
        totalNoOfDays: plan.total_no_of_days,
        difficulty: plan.difficulty,
        createdBy: plan.created_by,
        isPublic: plan.is_public,
        isTemplate: plan.is_template ?? true,
        templateId: plan.template_id,
        templateVersion: plan.template_version ?? 1,
        createdAt: plan.created_at,
        updatedAt: plan.updated_at,
        planWorkouts,
      } as WorkoutPlanWithSchedule;
    } catch (error) {
      console.error('Error in getWorkoutPlanViewById:', error);
      throw error;
    }
  }

  /**
   * Get workout count for a plan (lightweight; use instead of getWorkoutPlanById when only count is needed).
   */
  static async getPlanWorkoutCount(planId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('workouts_in_workoutplan')
        .select('*', { count: 'exact', head: true })
        .eq('workoutplan_id', planId);
      if (error) throw error;
      return count ?? 0;
    } catch (error) {
      console.error('Error in getPlanWorkoutCount:', error);
      throw error;
    }
  }

  /**
   * Plan row + schedule for duplicate flows only (no workout hydration).
   * @see `_bmad-output/planning-artifacts/workout-plan-duplicate-perf-handoff.md`
   */
  static async getWorkoutPlanDuplicateSource(planId: string): Promise<WorkoutPlanDuplicateSource | null> {
    try {
      const { data: plan, error: planError } = await supabase
        .from('workoutplan')
        .select('id, name, description, total_no_of_days, difficulty, created_by, is_public')
        .eq('id', planId)
        .maybeSingle();

      if (planError) {
        console.error('getWorkoutPlanDuplicateSource(plan):', planError);
        throw planError;
      }

      const { data: schedRows, error: schedError } = await fetchWorkoutsInPlanSchedule(
        planId,
        'workout_id, day_no, order_in_day',
      );

      if (schedError) {
        console.error('getWorkoutPlanDuplicateSource(schedule):', schedError);
        throw schedError;
      }

      if (!plan) return null;

      const schedule: PlanDuplicateScheduleRow[] = schedRows.map((r) => ({
        workoutId: r.workout_id,
        dayNo: r.day_no,
        orderInDay: r.order_in_day ?? 1,
      }));

      return {
        id: plan.id,
        name: plan.name ?? '',
        description: plan.description ?? null,
        totalNoOfDays: plan.total_no_of_days,
        difficulty: plan.difficulty ?? undefined,
        createdBy: plan.created_by,
        isPublic: !!plan.is_public,
        schedule,
      };
    } catch (error) {
      console.error('Error in getWorkoutPlanDuplicateSource:', error);
      throw error;
    }
  }

  private static async resolveUniqueTrainerLibraryTemplateName(baseLikeWorkoutDup: string): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      throw new Error('User not authenticated');
    }
    const { data: rows, error } = await supabase
      .from('workoutplan')
      .select('name')
      .eq('created_by', user.id)
      .eq('is_template', true);

    if (error) throw error;

    const takenLower = new Set((rows ?? []).map((r: { name: string }) => `${(r.name ?? '').trim()}`.toLowerCase()));
    const isTaken = (n: string) => takenLower.has(n.trim().toLowerCase());

    const baseTrim = baseLikeWorkoutDup.trim();
    if (!isTaken(baseTrim)) return baseTrim;

    for (let i = 1; i < 100000; i += 1) {
      const candidate = `${baseTrim} ${i}`;
      if (!isTaken(candidate)) return candidate;
    }
    throw new Error('Could not resolve a unique plan name.');
  }

  /**
   * Trainer library plan duplicate — **Option B**: `duplicate_trainer_workout_plan` RPC when deployed;
   * **Option A** client batch if RPC is missing/unavailable.
   * @see `database/duplicate_trainer_workout_plan_rpc.sql`
   */
  static async duplicateTrainerWorkoutPlan(sourcePlanId: string): Promise<DuplicateTrainerWorkoutPlanResult> {
    const t0 =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();

    try {
      const { data, error } = await supabase.rpc('duplicate_trainer_workout_plan', {
        p_source_plan_id: sourcePlanId,
        p_target_name: null,
      });

      if (error) {
        if (shouldFallbackDuplicateTrainerWorkoutPlanRpc(error)) {
          console.warn('[duplicateTrainerWorkoutPlan] RPC error; using client batch fallback', error);
          return WorkoutService.duplicateTrainerWorkoutPlanClientBatch(sourcePlanId);
        }
        console.error('[duplicateTrainerWorkoutPlan] rpc:', error);
        throw error;
      }

      const p = parseDuplicateTrainerWorkoutPlanRpcPayload(data);
      const result: DuplicateTrainerWorkoutPlanResult = {
        newPlanId: p.new_plan_id,
        sourcePlanId: p.source_plan_id,
        newPlanName: p.new_plan_name,
        scheduleRowsCopied: p.schedule_rows_copied,
      };

      try {
        await this.syncSessionsForPlanAssignments(result.newPlanId);
      } catch (w) {
        console.warn('[duplicateTrainerWorkoutPlan(RPC)] syncSessionsForPlanAssignments failed (non-fatal)', w);
      }

      const durationMs =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? Math.round(performance.now() - t0)
          : null;
      console.info('[duplicateTrainerWorkoutPlan]', {
        via: 'rpc',
        durationMs,
        source_plan_id: sourcePlanId,
        new_plan_id: result.newPlanId,
        new_plan_name: result.newPlanName,
        schedule_rows_copied: result.scheduleRowsCopied,
      });

      return result;
    } catch (e: unknown) {
      if (shouldFallbackDuplicateTrainerWorkoutPlanRpc(e)) {
        console.warn('[duplicateTrainerWorkoutPlan] RPC threw; using client batch fallback', e);
        return WorkoutService.duplicateTrainerWorkoutPlanClientBatch(sourcePlanId);
      }
      throw e;
    }
  }

  /** Option A fallback — unchanged behavior from pre-RPC path. */
  private static async duplicateTrainerWorkoutPlanClientBatch(
    sourcePlanId: string
  ): Promise<DuplicateTrainerWorkoutPlanResult> {
    const t0 =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();

    const source = await this.getWorkoutPlanDuplicateSource(sourcePlanId);
    if (!source) {
      throw Object.assign(new Error('Workout plan not found or not accessible.'), {
        code: 'SOURCE_PLAN_NOT_FOUND',
      });
    }

    const rawName = (source.name ?? '').trim();
    const baseRoot = rawName === '' ? 'Plan' : rawName;
    const baseCopy = `${baseRoot} (Copy)`;
    const newPlanName = await this.resolveUniqueTrainerLibraryTemplateName(baseCopy);

    const created = await this.createWorkoutPlan(
      {
        name: newPlanName,
        description: source.description ?? '',
        totalNoOfDays: source.totalNoOfDays,
        difficulty: source.difficulty as CreateWorkoutPlanData['difficulty'] | undefined,
        isPublic: false,
      },
      { returnMinimal: true }
    );

    const scheduleRowsCopied = source.schedule.length;
    try {
      if (scheduleRowsCopied > 0) {
        const bulk = source.schedule.map((r) => {
          const row: Record<string, string | number> = {
            workoutplan_id: created.id,
            workout_id: r.workoutId,
            day_no: r.dayNo,
          };
          if (orderInDayEnabled()) {
            row.order_in_day = r.orderInDay;
          }
          return row;
        });
        let { error: bulkError } = await supabase.from('workouts_in_workoutplan').insert(bulk);
        if (bulkError && isMissingOrderInDayError(bulkError)) {
          disableOrderInDaySupport();
          const legacyBulk = source.schedule.map((r) => ({
            workoutplan_id: created.id,
            workout_id: r.workoutId,
            day_no: r.dayNo,
          }));
          ({ error: bulkError } = await supabase.from('workouts_in_workoutplan').insert(legacyBulk));
        }
        if (bulkError) {
          console.error('duplicateTrainerWorkoutPlanClientBatch batch insert:', bulkError);
          throw bulkError;
        }
      }
    } catch (e) {
      try {
        await this.deleteWorkoutPlan(created.id);
      } catch {
        /* best-effort rollback */
      }
      throw e;
    }

    try {
      await this.syncSessionsForPlanAssignments(created.id);
    } catch (w) {
      console.warn(
        'duplicateTrainerWorkoutPlanClientBatch: syncSessionsForPlanAssignments failed (non-fatal)',
        w
      );
    }

    const durationMs =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? Math.round(performance.now() - t0)
        : null;

    console.info('[duplicateTrainerWorkoutPlan]', {
      via: 'fallback_client_batch',
      durationMs,
      source_plan_id: sourcePlanId,
      new_plan_id: created.id,
      new_plan_name: newPlanName,
      schedule_rows_copied: scheduleRowsCopied,
    });

    return {
      newPlanId: created.id,
      sourcePlanId,
      newPlanName,
      scheduleRowsCopied,
    };
  }

  /**
   * Lightweight count of a trainer's workouts. Uses a HEAD count query so no
   * rows are transferred — intended for tab badges that need a number before
   * the full list has loaded.
   */
  static async getWorkoutsCountForTrainer(trainerId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('workout')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', trainerId);
      if (error) throw error;
      return count ?? 0;
    } catch (error) {
      console.error('Error in getWorkoutsCountForTrainer:', error);
      throw error;
    }
  }

  /**
   * Lightweight count of a trainer's workout plan templates. HEAD count query
   * — see getWorkoutsCountForTrainer.
   */
  static async getWorkoutPlansCountForTrainer(trainerId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('workoutplan')
        .select('*', { count: 'exact', head: true })
        .eq('is_template', true)
        .eq('created_by', trainerId);
      if (error) throw error;
      return count ?? 0;
    } catch (error) {
      console.error('Error in getWorkoutPlansCountForTrainer:', error);
      throw error;
    }
  }

  /**
   * Get plan workouts (schedule) for a workout plan
   * Note: workout is optional because RLS may block access to individual workouts
   */
  static async getPlanWorkouts(workoutPlanId: string): Promise<(PlanWorkout & { workout?: WorkoutWithBlocks })[]> {
    
    try {
      // First, verify we can see the workout plan (for debugging)
      const { data: planCheck, error: planCheckError } = await supabase
        .from('workoutplan')
        .select('id, name, created_by, is_public')
        .eq('id', workoutPlanId)
        .maybeSingle();
      
      // Get current user ID for comparison
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id;

      if (planCheck && planCheck.created_by !== currentUserId && !planCheck.is_public) {
        console.warn('⚠️⚠️⚠️ RLS Warning: Plan created_by does not match current user ID!');
        console.warn('   Plan created_by:', planCheck.created_by);
        console.warn('   Current user ID:', currentUserId);
        console.warn('   Plan is_public:', planCheck.is_public);
      }
      
      const { data: planWorkouts, error: planWorkoutsError } = await fetchWorkoutsInPlanSchedule(
        workoutPlanId,
        '*',
      );

      if (planWorkoutsError) {
        const pgErr = planWorkoutsError as {
          code?: string;
          message?: string;
          details?: string;
          hint?: string;
        };
        console.error('❌ Error fetching plan workouts:', {
          error: planWorkoutsError,
          code: pgErr.code,
          message: pgErr.message,
          details: pgErr.details,
          hint: pgErr.hint,
          workoutPlanId,
          planCreatedBy: planCheck?.created_by,
        });

        if (
          pgErr.code === '42501' ||
          pgErr.message?.includes('permission') ||
          pgErr.message?.includes('policy')
        ) {
          console.error('🚫 RLS (Row Level Security) error detected!');
          console.error('💡 The trainer may not have permission to view workouts_in_workoutplan for this plan.');
          console.error('💡 Plan created_by:', planCheck?.created_by);
          console.error('💡 Check if the workout plan was created by the current trainer or if RLS policies need updating.');
        }
        
        throw planWorkoutsError;
      }

      if (!planWorkouts || planWorkouts.length === 0) {
        console.warn('💡 This could be an RLS issue. The workouts exist in the database but RLS is blocking access.');
        console.warn('💡 Plan details:', {
          id: planCheck?.id,
          name: planCheck?.name,
          createdBy: planCheck?.created_by,
          isPublic: planCheck?.is_public,
          currentUserId: currentUserId,
          userIdsMatch: planCheck?.created_by === currentUserId,
        });
        
        // Try a direct test query to see if RLS is the issue
        console.warn('🔍 Testing RLS access directly...');
        const { data: testQuery, error: testError } = await supabase
          .from('workouts_in_workoutplan')
          .select('id')
          .eq('workoutplan_id', workoutPlanId)
          .limit(1);
        
        console.warn('🔍 Direct test query result:', {
          foundAny: testQuery && testQuery.length > 0,
          error: testError,
          errorCode: testError?.code,
          errorMessage: testError?.message,
        });
        
        return [];
      }

      // Fetch workout details once per unique workout (dedupe)
      const uniqueWorkoutIds = Array.from(new Set(planWorkouts.map((pw) => pw.workout_id)));
      const workoutMap = new Map<string, WorkoutWithBlocks | null>();
      await Promise.all(
        uniqueWorkoutIds.map(async (workoutId) => {
          try {
            const fetched = await this.getWorkoutById(workoutId);
            workoutMap.set(workoutId, fetched);
            if (!fetched) {
              console.warn(`⚠️ getWorkoutById returned null for workout ${workoutId} (RLS may be blocking), but keeping plan workout entry for counting`);
            }
          } catch (error) {
            console.warn(`⚠️ Could not fetch workout ${workoutId} details (RLS may be blocking), but keeping plan workout entry for counting`);
            workoutMap.set(workoutId, null);
          }
        })
      );

      const planWorkoutsWithDetails = planWorkouts.map((pw) => {
        const workout = workoutMap.get(pw.workout_id) ?? undefined;
        return {
          id: pw.id,
          workoutPlanId: pw.workoutplan_id,
          workoutId: pw.workout_id,
          dayNo: pw.day_no,
          orderInDay: pw.order_in_day ?? 1,
          createdAt: pw.created_at,
          workout: workout || undefined,
        };
      });

      // Return all plan workouts, even if workout details couldn't be loaded
      // This ensures the count is correct even if RLS blocks individual workout access
      return planWorkoutsWithDetails as (PlanWorkout & { workout?: WorkoutWithBlocks })[];
    } catch (error) {
      console.error('Error in getPlanWorkouts:', error);
      throw error;
    }
  }

  /**
   * Create a new workout plan
   * @param options.returnMinimal Skip `getWorkoutPlanById` refetch (avoids hydrating schedule/workouts — use for dup/batch helpers).
   */
  static async createWorkoutPlan(
    planData: CreateWorkoutPlanData,
    options?: { returnMinimal?: boolean }
  ): Promise<WorkoutPlanWithSchedule> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Create the workout plan (as a template by default)
      const { data: plan, error: planError } = await supabase
        .from('workoutplan')
        .insert({
          name: planData.name,
          description: planData.description,
          total_no_of_days: planData.totalNoOfDays, // durationWeeks → totalNoOfDays
          difficulty: planData.difficulty,
          is_public: planData.isPublic,
          created_by: user.id, // created_by_trainer_id → created_by
          is_template: true, // New plans are templates by default
          template_version: 1, // Start at version 1
        })
        .select()
        .single();

      if (planError) {
        console.error('Error creating workout plan:', planError);
        throw planError;
      }

      // Note: Plan workouts (schedule) should be added separately after plan creation

      if (options?.returnMinimal) {
        return {
          id: plan.id,
          name: plan.name,
          description: plan.description,
          totalNoOfDays: plan.total_no_of_days,
          difficulty: plan.difficulty ?? undefined,
          createdBy: plan.created_by,
          isPublic: !!plan.is_public,
          isTemplate: plan.is_template ?? true,
          templateId: plan.template_id ?? undefined,
          templateVersion: plan.template_version ?? 1,
          createdAt: plan.created_at,
          updatedAt: plan.updated_at,
          planWorkouts: [],
        } as WorkoutPlanWithSchedule;
      }

      const completePlan = await this.getWorkoutPlanById(plan.id);
      if (!completePlan) {
        throw new Error('Failed to fetch created workout plan');
      }

      return completePlan;
    } catch (error) {
      console.error('Error in createWorkoutPlan:', error);
      throw error;
    }
  }

  /**
   * Create a plan workout (schedule entry)
   */
  static async createPlanWorkout(planWorkoutData: CreatePlanWorkoutData): Promise<PlanWorkout> {
    try {
      const baseInsert = {
        workoutplan_id: planWorkoutData.workoutPlanId,
        workout_id: planWorkoutData.workoutId,
        day_no: planWorkoutData.dayNo,
      };
      const insertWithOrder = {
        ...baseInsert,
        order_in_day: planWorkoutData.orderInDay ?? 1,
      };

      let insertPayload = orderInDayEnabled() ? insertWithOrder : baseInsert;
      let { data: planWorkout, error: planWorkoutError } = await supabase
        .from('workouts_in_workoutplan')
        .insert(insertPayload)
        .select()
        .single();

      if (planWorkoutError && isMissingOrderInDayError(planWorkoutError)) {
        disableOrderInDaySupport();
        ({ data: planWorkout, error: planWorkoutError } = await supabase
          .from('workouts_in_workoutplan')
          .insert(baseInsert)
          .select()
          .single());
      }

      if (planWorkoutError) {
        console.error('Error creating plan workout:', planWorkoutError);
        throw planWorkoutError;
      }

      // If this plan is assigned to members, sync their calendar sessions so new workouts appear (unless skipSync e.g. when extending start date backward)
      if (!planWorkoutData.skipSync) {
        await this.syncSessionsForPlanAssignments(planWorkoutData.workoutPlanId);
      }

      return {
        id: planWorkout.id,
        workoutPlanId: planWorkout.workoutplan_id,
        workoutId: planWorkout.workout_id,
        dayNo: planWorkout.day_no,
        orderInDay: planWorkout.order_in_day ?? 1,
        createdAt: planWorkout.created_at,
      };
    } catch (error) {
      console.error('Error in createPlanWorkout:', error);
      throw error;
    }
  }

  /**
   * When extending a plan's start date backward, shift existing plan workouts' day_no by +1
   * so the new day 1 slot is free. Update in descending day_no order to avoid unique constraint issues.
   */
  static async incrementPlanWorkoutDayNumbersFrom(workoutPlanId: string, fromDayNo: number): Promise<void> {
    const { data: rows, error: fetchError } = await supabase
      .from('workouts_in_workoutplan')
      .select('id, day_no')
      .eq('workoutplan_id', workoutPlanId)
      .gte('day_no', fromDayNo)
      .order('day_no', { ascending: false });

    if (fetchError || !rows?.length) return;

    for (const row of rows) {
      const { error: updateError } = await supabase
        .from('workouts_in_workoutplan')
        .update({ day_no: row.day_no + 1 })
        .eq('id', row.id);
      if (updateError) {
        console.error('Error incrementing day_no for plan workout:', updateError);
        throw new Error(`Failed to shift plan days: ${updateError.message}`);
      }
    }
  }

  /**
   * After plan workouts are added/removed/reordered, recreate workout_session rows for all members
   * assigned to this plan so the calendar stays in sync.
   */
  static async syncSessionsForPlanAssignments(workoutPlanId: string): Promise<void> {
    const { data: assignments, error } = await supabase
      .from('users_workoutplan')
      .select('id, user_id, start_date')
      .eq('workoutplan_id', workoutPlanId)
      .in('status', ['active', 'paused']);

    if (error) {
      console.warn('⚠️ Could not fetch assignments for plan sync:', error);
      return;
    }
    if (!assignments?.length) return;

    for (const a of assignments) {
      try {
        await this.createWorkoutSessionsFromPlan(a.user_id, workoutPlanId, a.start_date, a.id);
      } catch (err) {
        console.warn(`⚠️ Sync sessions failed for assignment ${a.id}:`, err);
      }
    }
  }

  /**
   * Update workout plan
   */
  static async updateWorkoutPlan(
    id: string,
    planData: Partial<CreateWorkoutPlanData>
  ): Promise<void> {
    try {
      const updateData: any = {};
      if (planData.name !== undefined) updateData.name = planData.name;
      if (planData.description !== undefined) updateData.description = planData.description;
      if (planData.totalNoOfDays !== undefined) updateData.total_no_of_days = planData.totalNoOfDays; // durationWeeks → totalNoOfDays
      if (planData.difficulty !== undefined) updateData.difficulty = planData.difficulty;
      if (planData.isPublic !== undefined) updateData.is_public = planData.isPublic;

      const { error } = await supabase
        .from('workoutplan')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('Error updating workout plan:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateWorkoutPlan:', error);
      throw error;
    }
  }

  /**
   * Delete workout plan
   */
  static async deleteWorkoutPlan(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('workoutplan')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting workout plan:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteWorkoutPlan:', error);
      throw error;
    }
  }

  // ============================================================================
  // MEMBER WORKOUT PLAN METHODS (Assignments)
  // ============================================================================

  /**
   * Assign a workout plan to a member
   * 
   * TEMPLATE/INSTANCE SYSTEM:
   * - Creates an instance copy of the template workout plan
   * - The instance is independent and won't be affected by template updates (unless trainer chooses to sync)
   * - Copies all workouts_in_workoutplan entries to the new instance
   * - Creates workout sessions from the instance
   * 
   * SECURITY NOTE: RLS is currently disabled on users_workoutplan and workout_session tables
   * due to policy issues. This function provides application-level validation:
   * - Verifies user is authenticated
   * - Verifies user is a trainer
   * - Verifies trainer owns the workout plan template
   * - Automatically creates workout sessions for the assigned plan
   * 
   * TODO: Fix RLS policies before production to add database-level security.
   * See: docs/RLS_SECURITY_ANALYSIS.md and TODO_RLS_FIX.md
   */
  static async assignPlanToMember(assignmentData: CreateMemberWorkoutPlanData): Promise<MemberWorkoutPlan> {
    try {
      console.log('🔍 assignPlanToMember - Attempting to assign plan:', {
        userId: assignmentData.userId,
        workoutPlanId: assignmentData.workoutPlanId,
        startDate: assignmentData.startDate,
      });
      
      // First, verify the workout plan template exists and get its details
      const { data: templatePlan, error: planError } = await supabase
        .from('workoutplan')
        .select('*')
        .eq('id', assignmentData.workoutPlanId)
        .single();
      
      if (planError || !templatePlan) {
        console.error('❌ Workout plan not found or cannot be accessed:', planError);
        throw new Error(`Workout plan not found: ${planError?.message || 'Unknown error'}`);
      }
      
      console.log('✅ Workout plan template found:', {
        id: templatePlan.id,
        name: templatePlan.name,
        createdBy: templatePlan.created_by,
        isTemplate: templatePlan.is_template,
        templateVersion: templatePlan.template_version,
      });
      
      // Verify the current user is the trainer who created this plan
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      if (templatePlan.created_by !== user.id) {
        throw new Error(`You can only assign workout plans that you created. This plan was created by a different trainer.`);
      }
      
      console.log('✅ Verified trainer owns the workout plan');
      
      // Additional debug: Check current user's role
      const { data: currentUserProfile } = await supabase
        .from('users')
        .select('id, role, email')
        .eq('id', user.id)
        .single();
      
      console.log('🔍 Current user profile:', currentUserProfile);
      
      if (!currentUserProfile || currentUserProfile.role !== 'trainer') {
        throw new Error(`Current user is not a trainer. Role: ${currentUserProfile?.role || 'unknown'}`);
      }
      
      // STEP 1: Create an instance copy of the template plan
      console.log('📋 Creating instance copy of template plan...');
      const { data: instancePlan, error: instanceError } = await supabase
        .from('workoutplan')
        .insert({
          name: templatePlan.name,
          description: templatePlan.description,
          total_no_of_days: templatePlan.total_no_of_days,
          difficulty: templatePlan.difficulty,
          is_public: false, // Instances are never public
          created_by: templatePlan.created_by,
          is_template: false, // This is an instance
          template_id: templatePlan.id, // Reference to the template
          template_version: templatePlan.template_version, // Capture current version
        })
        .select()
        .single();

      if (instanceError || !instancePlan) {
        console.error('❌ Error creating instance plan:', instanceError);
        throw new Error(`Failed to create plan instance: ${instanceError?.message || 'Unknown error'}`);
      }

      console.log('✅ Instance plan created:', {
        instanceId: instancePlan.id,
        templateId: instancePlan.template_id,
        version: instancePlan.template_version,
      });

      // STEP 2: Copy all workouts_in_workoutplan entries to the instance
      console.log('📋 Copying workout schedule to instance...');
      const { data: templateWorkouts, error: templateWorkoutsError } = await supabase
        .from('workouts_in_workoutplan')
        .select('workout_id, day_no')
        .eq('workoutplan_id', templatePlan.id)
        .order('day_no', { ascending: true });

      if (templateWorkoutsError) {
        console.error('❌ Error fetching template workouts:', templateWorkoutsError);
        throw new Error(`Failed to fetch template workouts: ${templateWorkoutsError.message}`);
      }

      if (!templateWorkouts || templateWorkouts.length === 0) {
        console.warn('⚠️ Template plan has no workouts');
      } else {
        const instanceWorkouts = templateWorkouts.map(tw => ({
          workoutplan_id: instancePlan.id,
          workout_id: tw.workout_id,
          day_no: tw.day_no,
        }));

        const { error: copyError } = await supabase
          .from('workouts_in_workoutplan')
          .insert(instanceWorkouts);

        if (copyError) {
          console.error('❌ Error copying workouts to instance:', copyError);
          throw new Error(`Failed to copy workouts to instance: ${copyError.message}`);
        }

        console.log(`✅ Copied ${instanceWorkouts.length} workouts to instance`);
      }

      // STEP 3: Create the assignment record (linking member to instance)
      const { data: assignment, error: assignmentError } = await supabase
        .from('users_workoutplan')
        .insert({
          user_id: assignmentData.userId,
          workoutplan_id: instancePlan.id, // Assign the INSTANCE, not the template
          start_date: assignmentData.startDate,
          notes: assignmentData.notes,
          status: 'active',
          assigned_by: user.id,
        })
        .select()
        .single();

      if (assignmentError) {
        console.error('❌ Error assigning plan to member:', assignmentError);
        console.error('❌ Error details:', {
          code: assignmentError.code,
          message: assignmentError.message,
          details: assignmentError.details,
          hint: assignmentError.hint,
        });
        throw assignmentError;
      }
      
      console.log('✅ Plan assigned successfully:', assignment);

      // STEP 4: Create workout sessions from the instance plan
      console.log('📅 Creating workout sessions from instance plan...');
      try {
        await this.createWorkoutSessionsFromPlan(assignment.user_id, instancePlan.id, assignment.start_date, assignment.id);
        console.log('✅ Workout sessions created successfully');
      } catch (sessionError: any) {
        console.error('⚠️ Error creating workout sessions:', sessionError);
        console.error('⚠️ Error details:', {
          message: sessionError?.message,
          code: sessionError?.code,
          details: sessionError?.details,
          hint: sessionError?.hint
        });
        // Don't throw - the assignment was successful, sessions can be created later
      }

      return {
        id: assignment.id,
        userId: assignment.user_id,
        workoutPlanId: assignment.workoutplan_id, // This is the instance ID
        startDate: assignment.start_date,
        status: assignment.status,
        endDate: assignment.end_date,
        pausedAt: assignment.paused_at,
        notes: assignment.notes,
        assignedBy: assignment.assigned_by ?? user.id,
        createdAt: assignment.created_at,
        updatedAt: assignment.updated_at,
      };
    } catch (error) {
      console.error('Error in assignPlanToMember:', error);
      throw error;
    }
  }

  /**
   * Create workout sessions from a workout plan assignment
   * @param assignmentId - users_workoutplan.id; used to scope deletes/inserts to this assignment only
   */
  static async createWorkoutSessionsFromPlan(
    userId: string,
    workoutPlanId: string,
    startDate: string,
    assignmentId?: string
  ): Promise<void> {
    try {
      console.log('🔍 Creating sessions for plan:', { userId, workoutPlanId, startDate });

      // Get all workouts in the plan with day numbers and intra-day slot order
      const { data: planWorkouts, error: planWorkoutsError } = await fetchWorkoutsInPlanSchedule(
        workoutPlanId,
        'workout_id, day_no, order_in_day',
      );

      if (planWorkoutsError) {
        throw new Error(`Failed to fetch plan workouts: ${(planWorkoutsError as { message?: string }).message ?? planWorkoutsError}`);
      }

      if (!planWorkouts || planWorkouts.length === 0) {
        console.warn('⚠️ No workouts found in plan:', workoutPlanId);
        return;
      }

      console.log(`📋 Found ${planWorkouts.length} workouts in plan`);

      const workoutIds = planWorkouts.map(pw => pw.workout_id);
      // Treat YYYY-MM-DD as a date-only key (timezone-agnostic).
      const startDateObj = parseUtcDateKey(startDate);
      const maxDayNo = Math.max(...planWorkouts.map((pw) => pw.day_no), 1);
      const rangeEnd = addDaysUtc(startDateObj, maxDayNo - 1);
      const rangeEndStr = toUtcDateKey(rangeEnd);

      // Delete existing sessions in this assignment's date range so we can recreate from current plan (handles add/remove/reorder)
      let deleteQuery = supabase
        .from('workout_session')
        .delete()
        .eq('user_id', userId)
        .gte('scheduled_date', startDate)
        .lte('scheduled_date', rangeEndStr);
      if (assignmentId) {
        deleteQuery = deleteQuery.eq('users_workoutplan_id', assignmentId);
      } else {
        deleteQuery = deleteQuery.in('workout_id', workoutIds);
      }
      const { error: deleteError } = await deleteQuery;

      if (deleteError) {
        console.warn('⚠️ Error deleting existing sessions in range:', deleteError);
        // Continue anyway - might be first time creating sessions
      }

      const { data: { user: actor } } = await supabase.auth.getUser();
      const assignedBy = actor?.id ?? null;

      // Create sessions for each workout
      const sessionsToInsert = planWorkouts.map((planWorkout) => {
        // Calculate scheduled date: start_date + (day_no - 1) days
        // Day 1 = start_date, Day 2 = start_date + 1 day, etc.
        const scheduledDate = addDaysUtc(startDateObj, planWorkout.day_no - 1);

        const session: Record<string, unknown> = {
          user_id: userId,
          workout_id: planWorkout.workout_id,
          scheduled_date: toUtcDateKey(scheduledDate),
          status: 'scheduled',
        };
        if (assignmentId) {
          session.users_workoutplan_id = assignmentId;
        }
        if (assignedBy) {
          session.assigned_by = assignedBy;
        }
        return session;
      });

      console.log(`📅 Creating ${sessionsToInsert.length} workout sessions...`);
      console.log('📝 Sessions to insert:', sessionsToInsert.slice(0, 3)); // Log first 3 for debugging

      const { data: insertedSessions, error: insertError } = await supabase
        .from('workout_session')
        .insert(sessionsToInsert)
        .select();

      if (insertError) {
        console.error('❌ Insert error details:', {
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
        });
        throw new Error(`Failed to create workout sessions: ${insertError.message}`);
      }

      // Snapshot each session's exercises (trainer's view at assignment time) so members see that version
      if (insertedSessions?.length) {
        const workoutCache = new Map<string, WorkoutWithBlocks | null>();
        const uniqueWorkoutIds = Array.from(new Set(insertedSessions.map((s) => s.workout_id)));
        await Promise.all(
          uniqueWorkoutIds.map(async (wid) => {
            workoutCache.set(wid, (await this.getWorkoutById(wid)) ?? null);
          }),
        );
        await Promise.all(
          insertedSessions.map((s) =>
            this.createSnapshotsForSession(
              s.id,
              s.workout_id,
              workoutCache.get(s.workout_id) ?? undefined,
            ),
          ),
        );
      }

      console.log(`✅ Successfully created ${insertedSessions?.length || sessionsToInsert.length} workout sessions`);
    } catch (error) {
      console.error('Error in createWorkoutSessionsFromPlan:', error);
      throw error;
    }
  }

  /**
   * Assign a single workout directly to a member (without a workout plan).
   * Creates a workout_session with users_workoutplan_id = null.
   */
  static async assignWorkoutToMember(
    userId: string,
    workoutId: string,
    scheduledDate: string
  ): Promise<WorkoutSession> {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('users')
        .select('role')
        .eq('id', authUser.id)
        .single();

      if (profile?.role !== 'trainer') {
        throw new Error('Only trainers can assign workouts');
      }

      // Verify workout exists
      const { data: workout, error: workoutError } = await supabase
        .from('workout')
        .select('id')
        .eq('id', workoutId)
        .single();

      if (workoutError || !workout) {
        throw new Error('Workout not found');
      }

      const { data: session, error: insertError } = await supabase
        .from('workout_session')
        .insert({
          user_id: userId,
          workout_id: workoutId,
          scheduled_date: scheduledDate,
          status: 'scheduled',
          users_workoutplan_id: null, // Direct assignment, not from a plan
          assigned_by: authUser.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Snapshot exercises (trainer's view at assignment) so member sees this version
      await this.createSnapshotsForSession(session.id, workoutId);

      return {
        id: session.id,
        userId: session.user_id,
        workoutId: session.workout_id,
        scheduledDate: session.scheduled_date,
        status: session.status,
        startedAt: session.started_at,
        completedAt: session.completed_at,
        actualDurationMinutes: session.actual_duration_minutes,
        notes: session.notes,
        usersWorkoutplanId: session.users_workoutplan_id ?? null,
        assignedBy: session.assigned_by ?? authUser.id,
        createdAt: session.created_at,
        updatedAt: session.updated_at,
      };
    } catch (error) {
      console.error('Error in assignWorkoutToMember:', error);
      throw error;
    }
  }

  /**
   * Get member's assigned workout plans
   */
  static async getMemberWorkoutPlans(memberId: string): Promise<MemberWorkoutPlan[]> {
    try {
      const { data: assignments, error: assignmentsError } = await supabase
        .from('users_workoutplan')
        .select('id, user_id, workoutplan_id, start_date, status, end_date, paused_at, notes, created_at, updated_at, assigned_by')
        .eq('user_id', memberId)
        .order('start_date', { ascending: false });

      if (assignmentsError && assignmentsError.code === '25P02') {
        return [];
      }
      if (assignmentsError) {
        console.error('Error fetching member workout plans:', assignmentsError);
        throw assignmentsError;
      }

      return (assignments || []).map((assignment) => ({
        id: assignment.id,
        userId: assignment.user_id, // memberId → userId
        workoutPlanId: assignment.workoutplan_id, // workout_plan_id → workoutplan_id
        startDate: assignment.start_date,
        status: assignment.status,
        endDate: assignment.end_date,
        pausedAt: assignment.paused_at,
        notes: assignment.notes,
        assignedBy: assignment.assigned_by ?? null,
        createdAt: assignment.created_at,
        updatedAt: assignment.updated_at,
      }));
    } catch (error) {
      console.error('Error in getMemberWorkoutPlans:', error);
      throw error;
    }
  }

  /**
   * Count rows in workouts_in_workoutplan per workout plan template id (actual # of workouts in the plan, not calendar days).
   */
  static async getScheduledWorkoutCountsByWorkoutPlanIds(
    workoutPlanIds: string[]
  ): Promise<Record<string, number>> {
    const unique = Array.from(new Set(workoutPlanIds.filter(Boolean)));
    if (unique.length === 0) return {};

    const counts: Record<string, number> = {};
    for (const id of unique) counts[id] = 0;

    const pageSize = 1000;
    let from = 0;
    try {
      for (;;) {
        const { data, error } = await supabase
          .from('workouts_in_workoutplan')
          .select('workoutplan_id')
          .in('workoutplan_id', unique)
          .range(from, from + pageSize - 1);

        if (error) throw error;
        const rows = data ?? [];
        for (const row of rows) {
          const pid = row.workoutplan_id as string;
          if (pid) counts[pid] = (counts[pid] ?? 0) + 1;
        }
        if (rows.length < pageSize) break;
        from += pageSize;
      }
      return counts;
    } catch (error) {
      console.error('Error in getScheduledWorkoutCountsByWorkoutPlanIds:', error);
      throw error;
    }
  }

  /**
   * Get member's assigned workout plans with light details (plan name, schedule with workout ids/names only).
   * Does not load full workout blocks/exercises. Use when listing plans; fetch full workout when user selects one.
   * When `trainerId` is set, only assignments with `users_workoutplan.assigned_by = trainerId` are returned.
   */
  static async getMemberWorkoutPlansWithLightDetails(memberId: string, trainerId?: string): Promise<(MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[]> {
    const t0 = performance.now();
    try {
      // Single query: assignments + plan name (embed workoutplan via FK) so we don't need a separate plans fetch
      const { data: rows, error: assignmentsError } = await supabase
        .from('users_workoutplan')
        .select('id, user_id, workoutplan_id, start_date, status, end_date, paused_at, notes, created_at, updated_at, assigned_by, workoutplan(id, name, total_no_of_days, created_by)')
        .eq('user_id', memberId)
        .order('start_date', { ascending: false });

      if (assignmentsError && assignmentsError.code === '25P02') {
        return [];
      }
      if (assignmentsError) {
        console.error('Error fetching member workout plans with details:', assignmentsError);
        throw assignmentsError;
      }
      console.warn(`[Perf] light plans: assignments+plan names ${(performance.now() - t0).toFixed(0)}ms`);

      const list = rows ?? [];
      if (list.length === 0) return [];

      const plansById = new Map<string, { id: string; name: string; total_no_of_days: number; created_by: string }>();
      for (const r of list) {
        const p = (r as { workoutplan?: unknown }).workoutplan;
        const plan = Array.isArray(p) ? p[0] : p;
        if (plan && typeof plan === 'object' && 'id' in plan)
          plansById.set(r.workoutplan_id, plan as { id: string; name: string; total_no_of_days: number; created_by: string });
      }

      // planWorkouts omitted here; Member Detail loads workout counts via getScheduledWorkoutCountsByWorkoutPlanIds.
      const result: (MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[] = [];
      for (const row of list) {
        const planRow = plansById.get(row.workoutplan_id);
        if (!planRow) continue;
        const assignedBy = (row as { assigned_by?: string | null }).assigned_by ?? null;
        if (trainerId && !trainerIdsMatch(assignedBy, trainerId)) continue;
        const planWorkouts: { id: string; workoutPlanId: string; workoutId: string; dayNo: number; createdAt?: string; workout?: { id: string; name: string } }[] = [];
        result.push({
          id: row.id,
          userId: row.user_id,
          workoutPlanId: row.workoutplan_id,
          startDate: row.start_date,
          status: row.status,
          endDate: row.end_date,
          pausedAt: row.paused_at,
          notes: row.notes,
          assignedBy,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          workoutPlan: {
            id: planRow.id,
            name: planRow.name,
            description: undefined,
            totalNoOfDays: planRow.total_no_of_days ?? 0,
            difficulty: undefined,
            createdBy: planRow.created_by,
            isPublic: false,
            isTemplate: true,
            templateId: undefined,
            templateVersion: 1,
            createdAt: '',
            updatedAt: '',
            planWorkouts: planWorkouts as (PlanWorkout & { workout?: WorkoutWithBlocks })[],
          } as WorkoutPlanWithSchedule,
        });
      }
      return result;
    } catch (error) {
      console.error('Error in getMemberWorkoutPlansWithLightDetails:', error);
      throw error;
    }
  }

  /**
   * Get member's assigned workout plans with full workout plan details
   * @param memberId - The member's user ID
   * @param trainerId - Optional trainer ID to filter plans created by this trainer
   */
  static async getMemberWorkoutPlansWithDetails(memberId: string, trainerId?: string): Promise<(MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[]> {
    try {
      const assignments = await this.getMemberWorkoutPlans(memberId);
      if (!assignments || assignments.length === 0) return [];

      const plansWithDetails = await Promise.all(
        assignments.map(async (assignment) => {
          try {
            const workoutPlan = await this.getWorkoutPlanById(assignment.workoutPlanId);
            if (!workoutPlan) return null;
            return { ...assignment, workoutPlan };
          } catch (err) {
            console.error(`Error fetching plan ${assignment.workoutPlanId}:`, err);
            return null;
          }
        })
      );

      let validPlans = plansWithDetails.filter((p) => p !== null && p.workoutPlan !== null) as (MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[];
      if (trainerId) {
        validPlans = validPlans.filter((p) => trainerIdsMatch(p.assignedBy, trainerId));
      }
      return validPlans;
    } catch (error) {
      console.error('❌ Error in getMemberWorkoutPlansWithDetails:', error);
      throw error;
    }
  }

  /**
   * Unassign a workout plan from a member.
   * Removes all workouts from the member's calendar that belong to this assignment,
   * except for completed ones. Then deletes the assignment record.
   */
  static async unassignPlanFromMember(assignmentId: string): Promise<void> {
    try {
      // 1. Delete all sessions for this assignment except completed
      const { error: deleteSessionsError } = await supabase
        .from('workout_session')
        .delete()
        .eq('users_workoutplan_id', assignmentId)
        .neq('status', 'completed');

      if (deleteSessionsError) {
        console.error('Error deleting sessions on unassign:', deleteSessionsError);
        throw new Error(`Failed to remove workouts from calendar: ${deleteSessionsError.message}`);
      }

      // 2. Delete the assignment record
      const { error: deleteAssignmentError } = await supabase
        .from('users_workoutplan')
        .delete()
        .eq('id', assignmentId);

      if (deleteAssignmentError) {
        console.error('Error deleting assignment:', deleteAssignmentError);
        throw new Error(`Failed to unassign plan: ${deleteAssignmentError.message}`);
      }
    } catch (error) {
      console.error('Error in unassignPlanFromMember:', error);
      throw error;
    }
  }

  /**
   * Update the start date of a plan assignment only. Does not move or recreate existing sessions.
   * Use this when adding a workout to a date before the current start (existing sessions stay on their dates).
   */
  static async updateAssignmentStartDate(assignmentId: string, newStartDate: string): Promise<void> {
    const { error: updateError } = await supabase
      .from('users_workoutplan')
      .update({ start_date: newStartDate })
      .eq('id', assignmentId);

    if (updateError) {
      console.error('Error updating assignment start date:', updateError);
      throw new Error(`Failed to update plan start date: ${updateError.message}`);
    }
  }

  /**
   * Create a single workout session for a plan assignment (e.g. when adding one workout to a plan
   * without re-syncing the whole plan, so existing sessions keep their dates).
   */
  static async createSingleSessionForPlanAssignment(
    assignmentId: string,
    workoutId: string,
    scheduledDate: string
  ): Promise<void> {
    const { data: assignment, error: fetchError } = await supabase
      .from('users_workoutplan')
      .select('user_id')
      .eq('id', assignmentId)
      .single();

    if (fetchError || !assignment) {
      throw new Error(`Assignment not found: ${fetchError?.message || 'Unknown'}`);
    }

    const { data: { user: actor } } = await supabase.auth.getUser();

    const { data: session, error: insertError } = await supabase
      .from('workout_session')
      .insert({
        user_id: assignment.user_id,
        workout_id: workoutId,
        scheduled_date: scheduledDate,
        status: 'scheduled',
        users_workoutplan_id: assignmentId,
        ...(actor?.id ? { assigned_by: actor.id } : {}),
      })
      .select()
      .single();

    if (insertError) throw insertError;
    await this.createSnapshotsForSession(session.id, workoutId);
  }

  /**
   * Lightweight check: latest update time for this member's dashboard data
   * (assignments + sessions). Used when caching wants a coarse “did anything change?” hint.
   */
  static async getMemberDashboardVersion(memberId: string): Promise<string | null> {
    try {
      const [assignmentsRes, sessionsRes] = await Promise.all([
        supabase
          .from('users_workoutplan')
          .select('updated_at')
          .eq('user_id', memberId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('workout_session')
          .select('updated_at')
          .eq('user_id', memberId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      const a = assignmentsRes.data?.updated_at ? new Date(assignmentsRes.data.updated_at).getTime() : 0;
      const s = sessionsRes.data?.updated_at ? new Date(sessionsRes.data.updated_at).getTime() : 0;
      const max = Math.max(a, s);
      return max > 0 ? new Date(max).toISOString() : null;
    } catch {
      return null;
    }
  }

  /** Event payload for dashboard Realtime so the UI can show a specific notification. */
  static readonly DashboardChangeTable = {
    users_workoutplan: 'users_workoutplan',
    workout_session: 'workout_session',
  } as const;
  static readonly DashboardChangeEvent = { INSERT: 'INSERT', UPDATE: 'UPDATE', DELETE: 'DELETE' } as const;

  /**
   * Subscribe to Realtime changes for this member's dashboard (assignments + sessions).
   * onUpdate is called with table and eventType so the UI can show e.g. "Your trainer assigned a new workout plan."
   * Enable Realtime first: run database_dashboard_realtime_enable.sql in Supabase.
   */
  static subscribeToMemberDashboardChanges(
    memberId: string,
    onUpdate: (payload: DashboardChangePayload) => void
  ): { unsubscribe: () => void } {
    const channel = supabase
      .channel(`dashboard:${memberId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'users_workoutplan',
          filter: `user_id=eq.${memberId}`,
        },
        (payload: { eventType: string }) => {
          onUpdate({
            table: 'users_workoutplan',
            eventType: payload.eventType as DashboardChangePayload['eventType'],
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workout_session',
          filter: `user_id=eq.${memberId}`,
        },
        (payload: { eventType: string }) => {
          onUpdate({
            table: 'workout_session',
            eventType: payload.eventType as DashboardChangePayload['eventType'],
          });
        }
      )
      .subscribe();
    return {
      unsubscribe: () => {
        if (channel?.unsubscribe) channel.unsubscribe();
      },
    };
  }

  /**
   * Get workout sessions for a member
   */
  static async getMemberWorkoutSessions(memberId: string, options?: {
    status?: WorkoutSession['status'];
    fromDate?: string;
    toDate?: string;
    limit?: number;
  }): Promise<(WorkoutSession & { workout?: WorkoutWithBlocks })[]> {
    try {
      let query = supabase
        .from('workout_session')
        .select('*')
        .eq('user_id', memberId) // member_id → user_id
        .order('scheduled_date', { ascending: true });

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.fromDate) {
        query = query.gte('scheduled_date', options.fromDate);
      }

      if (options?.toDate) {
        query = query.lte('scheduled_date', options.toDate);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data: sessions, error: sessionsError } = await query;

      if (sessionsError) {
        console.error('Error fetching workout sessions:', sessionsError);
        throw sessionsError;
      }

      const sessionList = sessions ?? [];
      if (sessionList.length === 0) return [];

      const sessionIds = sessionList.map((s) => s.id);
      const uniqueWorkoutIds = Array.from(new Set(sessionList.map((s) => s.workout_id)));

      // One batch for all snapshots; parallel fetch for each unique workout (no N per-session snapshot queries)
      const [snapshotsBySessionId, workoutsById] = await Promise.all([
        this.getSnapshotsBySessionIds(sessionIds),
        (async () => {
          const workoutList = await Promise.all(uniqueWorkoutIds.map((id) => this.getWorkoutById(id)));
          const map = new Map<string, WorkoutWithBlocks>();
          uniqueWorkoutIds.forEach((id, i) => {
            const w = workoutList[i];
            if (w) map.set(id, w);
          });
          return map;
        })(),
      ]);

      const sessionsWithWorkouts = sessionList.map((session) => {
        const workout = workoutsById.get(session.workout_id);
        const snapshots = snapshotsBySessionId.get(session.id) ?? [];
        let sessionWorkout: WorkoutWithBlocks | undefined = workout ?? undefined;
        if (workout && snapshots.length > 0) {
          // Clone so we don't mutate the cached workout (each session needs its own snapshot view)
          const clone: WorkoutWithBlocks = {
            ...workout,
            workoutBlocks: workout.workoutBlocks?.map((block) => ({
              ...block,
              exerciseSets: block.exerciseSets?.map((set) => ({ ...set })),
            })),
          };
          this.applySnapshotsToWorkout(clone, snapshots);
          sessionWorkout = clone;
        }
        return {
          id: session.id,
          userId: session.user_id,
          workoutId: session.workout_id,
          scheduledDate: session.scheduled_date,
          startedAt: session.started_at,
          completedAt: session.completed_at,
          status: session.status as WorkoutSession['status'],
          actualDurationMinutes: session.actual_duration_minutes,
          notes: session.notes,
          createdAt: session.created_at,
          updatedAt: session.updated_at,
          usersWorkoutplanId: session.users_workoutplan_id ?? undefined,
          workout: sessionWorkout,
        };
      });

      return sessionsWithWorkouts;
    } catch (error) {
      console.error('Error in getMemberWorkoutSessions:', error);
      throw error;
    }
  }

  /**
   * Per plan assignment (users_workoutplan.id): total scheduled session rows + completed count.
   * Total reflects current DB (trainer deletes shrink denominator); not the plan template totalNoOfDays.
   * Every assignment for the member is included with at least { total: 0, completed: 0 }.
   */
  static async getMemberPlanBannerStatsByAssignment(
    memberId: string
  ): Promise<Record<string, { total: number; completed: number }>> {
    const pageSize = 1000;
    try {
      const { data: assignments, error: assignError } = await supabase
        .from('users_workoutplan')
        .select('id')
        .eq('user_id', memberId);

      if (assignError) throw assignError;

      const stats: Record<string, { total: number; completed: number }> = {};
      for (const row of assignments ?? []) {
        stats[row.id as string] = { total: 0, completed: 0 };
      }

      let from = 0;
      for (;;) {
        const { data, error } = await supabase
          .from('workout_session')
          .select('users_workoutplan_id, status')
          .eq('user_id', memberId)
          .not('users_workoutplan_id', 'is', null)
          .range(from, from + pageSize - 1);

        if (error) throw error;
        const rows = data ?? [];
        for (const row of rows) {
          const id = row.users_workoutplan_id as string;
          if (!id) continue;
          if (!stats[id]) {
            stats[id] = { total: 0, completed: 0 };
          }
          stats[id].total += 1;
          if (row.status === 'completed') {
            stats[id].completed += 1;
          }
        }
        if (rows.length < pageSize) break;
        from += pageSize;
      }

      return stats;
    } catch (error) {
      console.error('Error in getMemberPlanBannerStatsByAssignment:', error);
      throw error;
    }
  }

  /**
   * Get workout sessions for a member for a date range only, with minimal workout info (name, duration).
   * Returns sessions in the range (plan-linked and standalone; no filter on users_workoutplan_id).
   * When `assignedByTrainerId` is set, only rows with `workout_session.assigned_by` matching that trainer
   * (trainer Members / Member Detail scope). Omit for member-facing calendar (all of their sessions).
   * Does not load full workout blocks/exercises or snapshots. Use for calendar; fetch full workout when user selects a session.
   */
  static async getMemberWorkoutSessionsLight(
    memberId: string,
    fromDate: string,
    toDate: string,
    assignedByTrainerId?: string
  ): Promise<
    (Omit<WorkoutSession, 'workout'> & {
      workout?: { id: string; name: string; estimatedDurationMinutes?: number; createdBy?: string };
      /** workoutplan.created_by when session is plan-linked (workout.created_by may be null on templates). */
      planCreatedBy?: string;
    })[]
  > {
    const t0 = performance.now();
    try {
      // Omit banner_image_url until DB has column (see database/exercise-maintenance/10-add-workout-banner-image-url.sql).
      const selectBase =
        'id, user_id, workout_id, scheduled_date, started_at, completed_at, status, actual_duration_minutes, notes, created_at, updated_at, users_workoutplan_id, assigned_by, workout:workout_id(id, name, estimated_duration_minutes, created_by)';
      const selectWithPlanEmbed = `${selectBase}, users_workoutplan:users_workoutplan_id(workoutplan:workoutplan_id(created_by))`;

      const runSelect = (sel: string) => {
        let q = supabase
          .from('workout_session')
          .select(sel)
          .eq('user_id', memberId)
          .gte('scheduled_date', fromDate)
          .lte('scheduled_date', toDate)
          .order('scheduled_date', { ascending: true });
        if (assignedByTrainerId) {
          q = q.eq('assigned_by', assignedByTrainerId);
        }
        return q;
      };

      let first = await runSelect(selectWithPlanEmbed);
      if (first.error) {
        console.warn('[getMemberWorkoutSessionsLight] Plan embed failed, retrying without:', first.error.message);
        first = await runSelect(selectBase);
      }
      const { data: sessionList, error: sessionsError } = first;
      if (sessionsError) throw sessionsError;
      const list = (sessionList ?? []) as unknown as Record<string, unknown>[];
      console.warn(`[Perf] light sessions: sessions+workout names ${(performance.now() - t0).toFixed(0)}ms (${list.length} rows)`);
      if (list.length === 0) return [];

      const mapped = list.map((session) => {
        const w = (session as { workout?: unknown }).workout;
        const workoutData = Array.isArray(w) ? w[0] : w;
        const wd =
          workoutData && typeof workoutData === 'object' && 'id' in workoutData
            ? (workoutData as {
                id: string;
                name: string;
                estimated_duration_minutes?: number;
                created_by?: string;
                createdBy?: string;
                banner_image_url?: string;
              })
            : null;
        const workout = wd
          ? {
              id: wd.id,
              name: wd.name,
              estimatedDurationMinutes: wd.estimated_duration_minutes ?? undefined,
              createdBy: wd.created_by ?? wd.createdBy ?? undefined,
              bannerImageUrl: wd.banner_image_url || undefined,
            }
          : undefined;

        const upRaw = (session as { users_workoutplan?: unknown }).users_workoutplan;
        const upRow = Array.isArray(upRaw) ? upRaw[0] : upRaw;
        let planCreatedBy: string | undefined;
        if (upRow && typeof upRow === 'object') {
          const wpRaw = (upRow as { workoutplan?: unknown }).workoutplan;
          const wpRow = Array.isArray(wpRaw) ? wpRaw[0] : wpRaw;
          if (wpRow && typeof wpRow === 'object') {
            const p = wpRow as { created_by?: string; createdBy?: string };
            planCreatedBy = p.created_by ?? p.createdBy ?? undefined;
          }
        }

        return {
          id: session.id as string,
          userId: session.user_id as string,
          workoutId: session.workout_id as string,
          scheduledDate: session.scheduled_date as string,
          startedAt: session.started_at as string | undefined,
          completedAt: session.completed_at as string | undefined,
          status: session.status as WorkoutSession['status'],
          actualDurationMinutes: session.actual_duration_minutes as number | undefined,
          notes: session.notes as string | undefined,
          createdAt: session.created_at as string,
          updatedAt: session.updated_at as string,
          usersWorkoutplanId: (session.users_workoutplan_id as string | null | undefined) ?? undefined,
          assignedBy: (session.assigned_by as string | null | undefined) ?? null,
          workout,
          planCreatedBy,
        };
      });

      if (!assignedByTrainerId) return mapped;
      return mapped.filter((s) => sessionIsFromTrainer(s, assignedByTrainerId));
    } catch (error) {
      console.error('Error in getMemberWorkoutSessionsLight:', error);
      throw error;
    }
  }

  /**
   * Get a single workout session by ID with workout details
   */
  static async getWorkoutSessionById(sessionId: string): Promise<(WorkoutSession & { workout?: WorkoutWithBlocks }) | null> {
    try {
      const { data: session, error: sessionError } = await supabase
        .from('workout_session')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError) {
        console.error('Error fetching workout session:', sessionError);
        throw sessionError;
      }

      if (!session) {
        return null;
      }

      // Fetch workout details
      let workout: WorkoutWithBlocks | undefined;
      console.log(`🔍 Fetching workout ${session.workout_id} for session ${session.id}...`);
      try {
        const fetchedWorkout = await this.getWorkoutById(session.workout_id);
        if (fetchedWorkout) {
          console.log(`✅ Workout fetched successfully:`, {
            id: fetchedWorkout.id,
            name: fetchedWorkout.name,
            blocksCount: fetchedWorkout.workoutBlocks?.length || 0
          });
          // Apply snapshots so member sees exercise as it was when assigned
          const snapshots = await this.getSessionExerciseSnapshots(session.id);
          this.applySnapshotsToWorkout(fetchedWorkout, snapshots);
          workout = fetchedWorkout;
        } else {
          console.warn(`⚠️ Workout ${session.workout_id} not found (getWorkoutById returned null)`);
          // Check if workout exists in database
          const { data: workoutCheck, error: workoutCheckError } = await supabase
            .from('workout')
            .select('id, name')
            .eq('id', session.workout_id)
            .maybeSingle();
          
          if (workoutCheckError) {
            console.error(`❌ Error checking workout existence:`, workoutCheckError);
          } else if (workoutCheck) {
            console.warn(`⚠️ Workout exists in DB but getWorkoutById returned null. Possible RLS issue or missing blocks.`);
          } else {
            console.error(`❌ Workout ${session.workout_id} does NOT exist in database!`);
          }
        }
      } catch (error: any) {
        console.error(`❌ Error fetching workout ${session.workout_id} for session ${session.id}:`, error);
        console.error(`Error details:`, {
          message: error?.message,
          code: error?.code,
          details: error?.details,
          hint: error?.hint
        });
      }

      return {
        id: session.id,
        userId: session.user_id, // memberId → userId
        workoutId: session.workout_id,
        usersWorkoutplanId: session.users_workoutplan_id ?? null,
        scheduledDate: session.scheduled_date,
        startedAt: session.started_at,
        completedAt: session.completed_at,
        status: session.status as WorkoutSession['status'],
        actualDurationMinutes: session.actual_duration_minutes,
        notes: session.notes,
        createdAt: session.created_at,
        updatedAt: session.updated_at,
        workout: workout,
      };
    } catch (error) {
      console.error('Error in getWorkoutSessionById:', error);
      throw error;
    }
  }

  /**
   * Update workout session status and optional fields
   */
  static async updateWorkoutSessionStatus(
    sessionId: string,
    status: WorkoutSession['status'],
    additionalData?: {
      startedAt?: string;
      completedAt?: string | null;
      actualDurationMinutes?: number | null;
      notes?: string;
    }
  ): Promise<void> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (additionalData?.startedAt) {
        updateData.started_at = additionalData.startedAt;
      }

      // Handle completedAt - can be set to null to clear it
      if (additionalData?.completedAt !== undefined) {
        updateData.completed_at = additionalData.completedAt;
      }

      // Handle actualDurationMinutes - can be set to null to clear it
      if (additionalData?.actualDurationMinutes !== undefined) {
        updateData.actual_duration_minutes = additionalData.actualDurationMinutes;
      }

      if (additionalData?.notes !== undefined) {
        updateData.notes = additionalData.notes;
      }

      const { error: updateError } = await supabase
        .from('workout_session')
        .update(updateData)
        .eq('id', sessionId);

      if (updateError) {
        console.error('Error updating workout session:', updateError);
        throw updateError;
      }
    } catch (error) {
      console.error('Error in updateWorkoutSessionStatus:', error);
      throw error;
    }
  }

  /**
   * Delete a workout session (e.g. when trainer removes a scheduled workout)
   */
  static async deleteWorkoutSession(sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('workout_session')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;
    } catch (error) {
      console.error('Error in deleteWorkoutSession:', error);
      throw error;
    }
  }

  /**
   * Update workout session scheduled date
   */
  static async updateWorkoutSessionDate(
    sessionId: string,
    newScheduledDate: string
  ): Promise<void> {
    try {
      const { error: updateError } = await supabase
        .from('workout_session')
        .update({
          scheduled_date: newScheduledDate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId);

      if (updateError) {
        console.error('Error updating workout session date:', updateError);
        throw updateError;
      }
    } catch (error) {
      console.error('Error in updateWorkoutSessionDate:', error);
      throw error;
    }
  }

  /**
   * Get all future workout sessions for a member (after the given date, not including the date itself)
   */
  static async getFutureWorkoutSessions(memberId: string, afterDate: string): Promise<WorkoutSession[]> {
    try {
      const { data: sessions, error: sessionsError } = await supabase
        .from('workout_session')
        .select('*')
        .eq('user_id', memberId)
        .gt('scheduled_date', afterDate)
        .order('scheduled_date', { ascending: true });

      if (sessionsError) {
        console.error('Error fetching future workout sessions:', sessionsError);
        throw sessionsError;
      }

      if (!sessions || sessions.length === 0) {
        return [];
      }

      return sessions.map((session) => ({
        id: session.id,
        userId: session.user_id,
        workoutId: session.workout_id,
        scheduledDate: session.scheduled_date,
        startedAt: session.started_at,
        completedAt: session.completed_at,
        status: session.status as WorkoutSession['status'],
        actualDurationMinutes: session.actual_duration_minutes,
        notes: session.notes,
        createdAt: session.created_at,
        updatedAt: session.updated_at,
      }));
    } catch (error) {
      console.error('Error in getFutureWorkoutSessions:', error);
      throw error;
    }
  }

  /**
   * Update multiple workout session scheduled dates
   */
  static async updateWorkoutSessionDates(
    sessionIds: string[],
    dateOffset: number // Number of days to add/subtract
  ): Promise<void> {
    try {
      // Get current sessions
      const { data: currentSessions, error: fetchError } = await supabase
        .from('workout_session')
        .select('id, scheduled_date')
        .in('id', sessionIds);

      if (fetchError) {
        console.error('Error fetching sessions for date update:', fetchError);
        throw fetchError;
      }

      if (!currentSessions || currentSessions.length === 0) {
        return;
      }

      // Update each session's date
      const updates = currentSessions.map(async (session) => {
        const currentDate = parseUtcDateKey(session.scheduled_date);
        const newDate = addDaysUtc(currentDate, dateOffset);
        const newDateString = toUtcDateKey(newDate);

        const { error: updateError } = await supabase
          .from('workout_session')
          .update({
            scheduled_date: newDateString,
            updated_at: new Date().toISOString(),
          })
          .eq('id', session.id);

        if (updateError) {
          console.error(`Error updating session ${session.id}:`, updateError);
          throw updateError;
        }
      });

      await Promise.all(updates);
    } catch (error) {
      console.error('Error in updateWorkoutSessionDates:', error);
      throw error;
    }
  }

  // ============================================================================
  // TEMPLATE/INSTANCE MANAGEMENT
  // ============================================================================

  /**
   * Get all active instances of a template plan
   * Returns members who have active assignments of this template
   */
  static async getActiveInstancesForTemplate(templateId: string): Promise<{
    instanceId: string;
    memberId: string;
    memberName: string;
    memberEmail: string;
    assignmentId: string;
    startDate: string;
    templateVersion: number;
  }[]> {
    try {
      console.log('🔍 Getting active instances for template:', templateId);

      // Find all instance plans that reference this template
      const { data: instances, error: instancesError } = await supabase
        .from('workoutplan')
        .select('id, template_version')
        .eq('template_id', templateId)
        .eq('is_template', false);

      if (instancesError) {
        console.error('❌ Error fetching instances:', instancesError);
        throw instancesError;
      }

      if (!instances || instances.length === 0) {
        console.log('📋 No instances found for this template');
        return [];
      }

      const instanceIds = instances.map(i => i.id);
      console.log(`📋 Found ${instances.length} instances:`, instanceIds);

      // Find active assignments for these instances
      const { data: assignments, error: assignmentsError } = await supabase
        .from('users_workoutplan')
        .select(`
          id,
          user_id,
          workoutplan_id,
          start_date,
          status
        `)
        .in('workoutplan_id', instanceIds)
        .eq('status', 'active');

      if (assignmentsError) {
        console.error('❌ Error fetching assignments:', assignmentsError);
        throw assignmentsError;
      }

      if (!assignments || assignments.length === 0) {
        console.log('📋 No active assignments found for instances');
        return [];
      }

      console.log(`📋 Found ${assignments.length} active assignments`);

      // Get member details
      const memberIds = assignments.map(a => a.user_id);
      const { data: members, error: membersError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name')
        .in('id', memberIds);

      if (membersError) {
        console.error('❌ Error fetching members:', membersError);
        throw membersError;
      }

      // Combine the data
      const result = assignments.map(assignment => {
        const instance = instances.find(i => i.id === assignment.workoutplan_id);
        const member = members?.find(m => m.id === assignment.user_id);

        return {
          instanceId: assignment.workoutplan_id,
          memberId: assignment.user_id,
          memberName: displayNameFromProfile(member) || 'Unknown',
          memberEmail: member?.email || '',
          assignmentId: assignment.id,
          startDate: assignment.start_date,
          templateVersion: instance?.template_version || 1,
        };
      });

      console.log(`✅ Returning ${result.length} active instances with member details`);
      return result;
    } catch (error) {
      console.error('Error in getActiveInstancesForTemplate:', error);
      throw error;
    }
  }

  /**
   * Sync an instance plan with its template
   * Updates the instance to match the current template structure
   * 
   * IMPORTANT: Preserves completed sessions (history)
   * - Only deletes sessions where scheduled_date >= today AND status != 'completed'
   * - Creates new sessions from updated schedule for dates >= today
   */
  static async syncInstanceWithTemplate(instanceId: string): Promise<void> {
    try {
      console.log('🔄 Syncing instance with template:', instanceId);

      const updateDate = new Date().toISOString().split('T')[0]; // Today's date
      console.log('📅 Update date (cutoff):', updateDate);

      // Get the instance plan
      const { data: instance, error: instanceError } = await supabase
        .from('workoutplan')
        .select('*')
        .eq('id', instanceId)
        .eq('is_template', false)
        .single();

      if (instanceError || !instance) {
        throw new Error(`Instance plan not found: ${instanceError?.message || 'Unknown error'}`);
      }

      if (!instance.template_id) {
        throw new Error('This plan is not an instance (no template_id)');
      }

      console.log('📋 Instance found:', {
        instanceId: instance.id,
        templateId: instance.template_id,
        currentVersion: instance.template_version,
      });

      // Get the template plan
      const { data: template, error: templateError } = await supabase
        .from('workoutplan')
        .select('*')
        .eq('id', instance.template_id)
        .single();

      if (templateError || !template) {
        throw new Error(`Template plan not found: ${templateError?.message || 'Unknown error'}`);
      }

      console.log('📋 Template found:', {
        templateId: template.id,
        templateVersion: template.template_version,
      });

      // Update instance metadata to match template
      const { error: updateError } = await supabase
        .from('workoutplan')
        .update({
          name: template.name,
          description: template.description,
          total_no_of_days: template.total_no_of_days,
          difficulty: template.difficulty,
          template_version: template.template_version,
          updated_at: new Date().toISOString(),
        })
        .eq('id', instanceId);

      if (updateError) {
        throw new Error(`Failed to update instance metadata: ${updateError.message}`);
      }

      console.log('✅ Instance metadata updated');

      // Delete existing workouts_in_workoutplan for instance
      const { error: deleteError } = await supabase
        .from('workouts_in_workoutplan')
        .delete()
        .eq('workoutplan_id', instanceId);

      if (deleteError) {
        throw new Error(`Failed to delete old instance workouts: ${deleteError.message}`);
      }

      console.log('✅ Old instance workouts deleted');

      // Copy current template workouts to instance
      const { data: templateWorkouts, error: templateWorkoutsError } = await supabase
        .from('workouts_in_workoutplan')
        .select('workout_id, day_no')
        .eq('workoutplan_id', template.id)
        .order('day_no', { ascending: true });

      if (templateWorkoutsError) {
        throw new Error(`Failed to fetch template workouts: ${templateWorkoutsError.message}`);
      }

      if (templateWorkouts && templateWorkouts.length > 0) {
        const instanceWorkouts = templateWorkouts.map(tw => ({
          workoutplan_id: instanceId,
          workout_id: tw.workout_id,
          day_no: tw.day_no,
        }));

        const { error: copyError } = await supabase
          .from('workouts_in_workoutplan')
          .insert(instanceWorkouts);

        if (copyError) {
          throw new Error(`Failed to copy template workouts to instance: ${copyError.message}`);
        }

        console.log(`✅ Copied ${instanceWorkouts.length} workouts to instance`);
      }

      // Get the assignment to update sessions
      const { data: assignment, error: assignmentError } = await supabase
        .from('users_workoutplan')
        .select('id, user_id, start_date')
        .eq('workoutplan_id', instanceId)
        .single();

      if (assignmentError || !assignment) {
        console.warn('⚠️ Could not find assignment for instance, skipping session update');
        return;
      }

      // ============================================================
      // SMART SESSION UPDATE: Preserve history, update future only
      // ============================================================

      console.log('📅 Updating workout sessions (preserving completed history)...');

      // Step 1: Get existing sessions for this user that belong to THIS assignment only
      // Only touch sessions with users_workoutplan_id = assignment.id (don't touch direct assignments or other plans)
      const { data: existingSessions, error: sessionsError } = await supabase
        .from('workout_session')
        .select('id, workout_id, scheduled_date, status')
        .eq('user_id', assignment.user_id)
        .eq('users_workoutplan_id', assignment.id);

      if (sessionsError) {
        throw new Error(`Failed to fetch existing sessions: ${sessionsError.message}`);
      }

      // Step 2: Identify sessions to keep (completed OR before update date)
      const sessionsToKeep = (existingSessions || []).filter(session => {
        const isCompleted = session.status === 'completed';
        const isBeforeUpdateDate = session.scheduled_date < updateDate;
        return isCompleted || isBeforeUpdateDate;
      });

      // Step 3: Identify sessions to delete (on/after update date AND not completed)
      const sessionIdsToDelete = (existingSessions || [])
        .filter(session => {
          const isOnOrAfterUpdateDate = session.scheduled_date >= updateDate;
          const isNotCompleted = session.status !== 'completed';
          return isOnOrAfterUpdateDate && isNotCompleted;
        })
        .map(session => session.id);

      console.log(`📊 Sessions: ${existingSessions?.length || 0} total, ${sessionsToKeep.length} to keep, ${sessionIdsToDelete.length} to delete`);

      // Step 4: Delete non-completed future sessions
      if (sessionIdsToDelete.length > 0) {
        const { error: deleteSessionsError } = await supabase
          .from('workout_session')
          .delete()
          .in('id', sessionIdsToDelete);

        if (deleteSessionsError) {
          throw new Error(`Failed to delete old sessions: ${deleteSessionsError.message}`);
        }

        console.log(`✅ Deleted ${sessionIdsToDelete.length} non-completed future sessions`);
      }

      // Step 5: Create new sessions from updated schedule for dates >= update date
      if (templateWorkouts && templateWorkouts.length > 0) {
        const startDateObj = new Date(assignment.start_date);
        
        // Get dates that already have completed sessions (to avoid duplicates)
        const completedDates = new Set(
          sessionsToKeep
            .filter(s => s.status === 'completed')
            .map(s => s.scheduled_date)
        );

        const newSessions: Record<string, unknown>[] = [];
        const { data: { user: actor } } = await supabase.auth.getUser();
        const assignedBy = actor?.id ?? null;

        for (const tw of templateWorkouts) {
          // Calculate scheduled date: start_date + (day_no - 1)
          const scheduledDate = new Date(startDateObj);
          scheduledDate.setDate(startDateObj.getDate() + (tw.day_no - 1));
          const scheduledDateStr = scheduledDate.toISOString().split('T')[0];

          // Only create session if:
          // 1. Date is on or after update date
          // 2. No completed session exists for this date
          if (scheduledDateStr >= updateDate && !completedDates.has(scheduledDateStr)) {
            const session: Record<string, unknown> = {
              user_id: assignment.user_id,
              workout_id: tw.workout_id,
              scheduled_date: scheduledDateStr,
              status: 'scheduled',
              users_workoutplan_id: assignment.id,
            };
            if (assignedBy) {
              session.assigned_by = assignedBy;
            }
            newSessions.push(session);
          }
        }

        if (newSessions.length > 0) {
          const { error: insertError } = await supabase
            .from('workout_session')
            .insert(newSessions);

          if (insertError) {
            throw new Error(`Failed to create new sessions: ${insertError.message}`);
          }

          console.log(`✅ Created ${newSessions.length} new sessions for dates >= ${updateDate}`);
        } else {
          console.log('ℹ️ No new sessions needed (all future dates have completed sessions)');
        }
      }

      console.log('✅ Instance synced successfully with template (history preserved)');
    } catch (error) {
      console.error('Error in syncInstanceWithTemplate:', error);
      throw error;
    }
  }

  /**
   * Sync multiple instances with their template
   */
  static async syncMultipleInstancesWithTemplate(instanceIds: string[]): Promise<void> {
    try {
      console.log(`🔄 Syncing ${instanceIds.length} instances with template...`);

      for (const instanceId of instanceIds) {
        try {
          await this.syncInstanceWithTemplate(instanceId);
        } catch (error) {
          console.error(`❌ Failed to sync instance ${instanceId}:`, error);
          // Continue with other instances even if one fails
        }
      }

      console.log('✅ Batch sync completed');
    } catch (error) {
      console.error('Error in syncMultipleInstancesWithTemplate:', error);
      throw error;
    }
  }
}
